import { hkLine } from '../components/HkFields';
import { dpPass } from '../components/LeakTest';
import { companyLine } from './company';
import { stakeStats } from './stake';
// signed distance: cm below 1 m, else m (decimal comma)
const dm = (v) => (Math.abs(v) < 1 ? `${(v * 100).toFixed(1).replace('.', ',')} cm` : `${v.toFixed(2).replace('.', ',')} m`);
import { cpTol, cpEval, cpCm } from './controlPts';
import { cbColor } from './a11y';
import { getCrew, STEPS, jobTimes, hm, calDue, staffCost } from './crew';
import { cur } from './units';
import { jsPDF } from 'jspdf';
import { I18N, ROLE_KEYS } from './i18n';
import { tooFlat, PIPE_TYPES, FITTING_TYPES, FIT_STATES, pipeLatLngs, pathLength } from './model';
import { fmtDur } from './media';
import { APP_INFO, getLogo, getPrices, findPrice, eur, fill } from './license';
import { countEntries, countTotals } from './inventory';
import { clean, loadImg, mapSnapshot, overviewView, fileStem, projectStem } from './exporters';
import { dirText, geoPhotos } from './photoDir';
import { bldHas, bldText, reachHead, inspItems, dmgLine, CLS_RGB } from './sewerInsp';
import { materialRows, fmtQty } from './materialList';
import { civilChecks } from './civilCheck';
import { coordTable } from '../components/CoordList';
import { planBounds } from '../components/FloorPlans';
import { moistBlock, moistTrendBlock, moistPins } from './moistPdf';
import { wallGroups } from './moist';
import { mo2 } from './i18nMoist2';
import { objPhotoAppendix } from './objReportPdf';
import { panoXY } from '../components/Pano360';
import { getMode } from './profiles';
import { irrSummary, proposal, hhmm } from './irrigation';
import { areaCoverage } from './coverage';
import { computePit, pitOverview, pitRing, layerName, unitLabel, f2 } from './excavation';
import { vatSplit } from './vat';
import { MAINT, objPos, damagePoints } from './maint';
import { gnssLine, FIX_SHORT } from './gnss';
import { profileData } from './profile';
import { allSurveys, surveyDims, surveyText, offsetM } from './survey';
import { pitClashes } from './pitClash';
import { planCheck } from './planCheck';
import { pressureMap } from './pressure';
import { leakFlow, flowSaved } from './leakCalc';
import { rowCalc, landCalc } from './garden';
import { drawSummary, LINE_STYLES, PATTERNS } from './drawStyles';
import { dailyTotals } from '../components/DailyReport';
import { redText, isRot } from '../components/FittingExtras';
import { fitArms, armState } from './fitSymbols';
import { loggerState, fmtDuration, noiseSpots } from '../hooks/useLoggers';
import { pipeUtilText, hydRow, HYD_MIN_Q, gasLeaks, gasLeakRow, gasLeakHead } from './utility';
import { logRows, logHead } from './trackModes';
import { kksPoints, kksRow, kksHead, kksOk, cfPins, cfText, locPoints, locText, scafState, kksChart, kksTrend, kksHist, xbPoints, xbText } from './inspect';
import { tl } from './terms';
import { ml, modeT, modeId } from './profiles';
import { feat } from './features';
import { locale as appLocale } from './units';
import { areaSummary } from './pmSummary';
import { pmT } from './i18nPm';
const nf = (v, d) => (+v || 0).toLocaleString(appLocale(), { minimumFractionDigits: d, maximumFractionDigits: d });

const M = 15, PW = 210, PH = 297, CW = PW - 2 * M;
const hex = (c = '#94a3b8') => [1, 3, 5].map((i) => parseInt(c.slice(i, i + 2), 16));
const mix = (a, b, k) => a.map((v, i) => Math.round(v + (b[i] - v) * k));

export const pdfName = (project, reports, opts = {}) => {
  const proj = opts.cover ?? (project.mode !== 'single' && reports.length > 1);
  return `${proj ? `${projectStem(project)}_Projekt${opts.area ? `_${opts.area.replace(/[^\p{L}\p{N}]+/gu, '-')}` : ''}` : fileStem(reports[0].title, reports[0].address)}.pdf`;
};

export async function exportPDF(project, reports, lang, opts = {}) {
  const t = modeT(tl(lang));
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const fittings = project.fittings || [];
  const pipes = (project.pipes || []).map((pp) => ({ ...pp, pts: pipeLatLngs(pp, fittings), color: cbColor(pp.color || PIPE_TYPES[pp.type] || PIPE_TYPES.other) })).filter((pp) => pp.pts.length >= 2);
  const fmt = (ts) => new Date(ts || Date.now()).toLocaleString(t.locale);
  const isProject = opts.cover ?? (project.mode !== 'single' && reports.length > 1);
  const projectPt = project.mode !== 'single' && project.lat != null ? { lat: project.lat, lon: project.lon, name: project.title } : null;

  // trim text to a column width (mm) so nothing runs past the right margin
  const fitW = (v, w) => { let x = clean(String(v ?? '')); if (doc.getTextWidth(x) <= w) return x; while (x.length > 1 && doc.getTextWidth(x + '..') > w) x = x.slice(0, -1); return x + '..'; };
  const colW = (cols, i) => (i < cols.length - 1 ? cols[i + 1] - cols[i] : cols[i] - cols[i - 1]) - 2;
  const logo = getLogo(), coLine = companyLine();
  let logoDim = null;
  if (logo) {
    const li = await loadImg(logo, false);
    if (li) { const r = li.width / li.height; const w = Math.min(36, r * 14); logoDim = { w, h: w / r }; }
  }

  const meta = { 1: null };
  let ctx = null;
  let y = 0;
  const setCtx = (c) => { ctx = c; meta[doc.getNumberOfPages()] = c; y = c.top; };
  const newPage = (c = ctx) => { doc.addPage(); setCtx(c); };
  const ensure = (h) => { if (y + h > PH - 18) newPage(); };
  const projCtx = { kind: 'project', top: 40 };

  const drawLogo = (x, yc, maxH = 99) => {
    if (!logoDim) return x;
    const k = Math.min(1, maxH / logoDim.h), w = logoDim.w * k, h = logoDim.h * k;
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(x - 1.5, yc - h / 2 - 1.5, w + 3, h + 3, 1.5, 1.5, 'F');
    doc.addImage(logo, 'JPEG', x, yc - h / 2, w, h);
    return x + w + 5;
  };
  // one banner line: shrink to min size, then cut with '..'
  const fitLine = (txt, size, min, w) => {
    let fs = size, s = clean(txt || ''); doc.setFontSize(fs);
    while (fs > min && doc.getTextWidth(s) > w) { fs -= 0.5; doc.setFontSize(fs); }
    if (doc.getTextWidth(s) > w) { while (s.length > 1 && doc.getTextWidth(`${s}..`) > w) s = s.slice(0, -1); s = `${s.trimEnd()}..`; }
    return s;
  };

  const projectBanner = () => {
    const a = hex('#0A2540'), b = hex('#1565C0');
    for (let i = 0; i < 60; i++) { doc.setFillColor(...mix(a, b, i / 59)); doc.rect((PW / 60) * i, 0, PW / 60 + 0.3, 30, 'F'); }
    doc.setFillColor(250, 204, 21); doc.rect(0, 30, PW, 3, 'F');
    const tx = drawLogo(M, 15);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); const pw = doc.getTextWidth(APP_INFO.publisher);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); const dw = doc.getTextWidth(clean(fmt(project.updatedAt)));
    doc.setTextColor(250, 204, 21); doc.setFont('helvetica', 'bold');
    doc.text(fitLine(t.bnProject, 10, 6.5, PW - M - pw - 5 - tx), tx, 12);
    doc.setTextColor(255, 255, 255);
    doc.text(fitLine(project.title, 14, 10, PW - M - dw - 5 - tx), tx, 21);
    doc.setFontSize(13); doc.text(APP_INFO.publisher, PW - M, 13, { align: 'right' });
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
    doc.text(clean(fmt(project.updatedAt)), PW - M, 21, { align: 'right' });
  };

  const reportBanner = (c) => {
    const H = c.compact ? 22 : 30;
    doc.setFillColor(230, 81, 0); doc.rect(0, 0, PW, H, 'F');
    doc.setFillColor(2, 132, 199); doc.rect(0, H, PW, 2.2, 'F');
    doc.setDrawColor(255, 255, 255); doc.setLineWidth(0.3); doc.rect(4, 3, PW - 8, H - 6);
    doc.setFillColor(2, 132, 199); doc.rect(M - 4, 6, 30, H - 12, 'F');
    doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5); doc.text(clean(c.badge || t.bnPoint), M + 11, 6 + (H - 12) / 2 - 2, { align: 'center' });
    doc.setFontSize(c.compact ? 11 : 14); doc.text(c.num ? `#${c.num}` : '', M + 11, 6 + (H - 12) / 2 + 4.5, { align: 'center' });
    const tx = drawLogo(M + 31, H / 2, H - 12);
    // right block (publisher, date, coordinates) first, then label / name shrink into the space left of it
    doc.setFontSize(c.compact ? 10 : 12); const pw = doc.getTextWidth(APP_INFO.publisher);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); const dw = Math.max(c.date ? doc.getTextWidth(clean(c.date)) : 0, c.coords ? doc.getTextWidth(c.coords) : 0);
    doc.setFont('helvetica', 'bold');
    const lab = clean(c.label || t.bnReport), avL = PW - M + 2 - pw - 5 - tx, avN = PW - M + 2 - dw - 5 - tx;
    let fs = c.compact ? 8 : 9.5; doc.setFontSize(fs);
    while (fs > 6.5 && doc.getTextWidth(lab) > avL) { fs -= 0.5; doc.setFontSize(fs); }
    const LL = doc.splitTextToSize(lab, avL).slice(0, 2), ly = c.compact ? 10 : 12;
    doc.text(LL, tx, LL.length > 1 ? ly - 2.2 : ly, { lineHeightFactor: 1.1 });
    doc.text(fitLine(c.name, c.compact ? 10 : 13, 9, avN), tx, c.compact ? 16.5 : 21);
    doc.setFontSize(c.compact ? 10 : 12); doc.text(APP_INFO.publisher, PW - M + 2, c.compact ? 10 : 11.5, { align: 'right' });
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5);
    if (c.date) doc.text(clean(c.date), PW - M + 2, c.compact ? 15 : 18, { align: 'right' });
    if (c.coords) doc.text(c.coords, PW - M + 2, c.compact ? 18.5 : 22.5, { align: 'right' });
  };

  const section = (title) => {
    ensure(14);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(10, 37, 64);
    doc.text(clean(title), M, y);
    doc.setDrawColor(ctx?.kind === 'project' ? 250 : 230, ctx?.kind === 'project' ? 204 : 81, ctx?.kind === 'project' ? 21 : 0); doc.setLineWidth(0.6);
    doc.line(M, y + 1.5, M + CW, y + 1.5);
    doc.setTextColor(20, 20, 20); y += 8;
  };
  const kv = (k, v) => {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.text(clean(k), M, y);
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(clean(v), CW - 48);
    doc.text(lines, M + 48, y); y += Math.max(6, lines.length * 5);
  };
  const para = (txt, size = 10, x = M, w = CW) => {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(size);
    doc.splitTextToSize(clean(txt), w).forEach((ln) => { ensure(size / 2); doc.text(ln, x, y); y += size * 0.48; });
  };
  const memoText = (memos) => (memos || []).filter((m) => m.transcript).map((m) => `${t.pdfMemo} (${fmtDur(m.duration)}): "${m.transcript}"`).join('  ');
  const legend = () => {
    const symF = [...new Map(fittings.filter((f) => f.type === 'other' && f.sym).map((f) => [f.sym, f])).values()];
    const usedP = [...new Set(pipes.map((p) => p.type))], usedF = [...new Set(fittings.filter((f) => !(f.type === 'other' && f.sym)).map((f) => f.type))];
    if (!usedP.length && !usedF.length && !symF.length) return;
    ensure(10);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.text(clean(t.pdfLegend + ':'), M, y);
    let x = M + 18;
    const item = (draw, label) => {
      const w = doc.getTextWidth(clean(label)) + 10;
      if (x + w > M + CW) { x = M + 18; y += 5; }
      draw(x); doc.setFont('helvetica', 'normal'); doc.text(clean(label), x + 7, y); x += w + 2;
    };
    usedP.forEach((k) => item((x0) => { doc.setFillColor(...hex(PIPE_TYPES[k])); doc.rect(x0, y - 2, 5.5, 1.6, 'F'); }, t['type_' + k]));
    usedF.forEach((k) => item((x0) => { doc.setFillColor(...hex(FITTING_TYPES[k])); doc.circle(x0 + 2.5, y - 1.2, 2, 'F'); }, `${t['fab_' + k]} = ${t['fit_' + k]}`));
    symF.forEach((f) => item((x0) => { doc.setFillColor(...hex(f.color || FITTING_TYPES.other)); doc.circle(x0 + 2.5, y - 1.2, 2, 'F'); }, `${f.sym} = ${f.cat?.name || f.name}`));
    y += 7;
  };
  const pits = project.pits || [];
  const areaName = (x) => (x.sub ? t[`dsub_${x.sub}`] || x.sub : pmT().br[x.branch] || x.branch);
  // summary per area (water leak, gas leak, civil works ...) - counts, period, teams, planned/actual cost
  const areaTable = () => {
    if (!project.pm) return;
    const A = areaSummary(project, reports), P = pmT(); if (!A.length) return;
    y += 3; section(opts.area ? `${P.sumT} · ${P.pdfFor}: ${opts.area}` : P.sumT);
    const X = [M, M + 66, M + 71, M + 152, M + CW], W = [48, 16, 44, 30, 26], R = [0, 1, 0, 1, 1], d = (v) => (v ? new Date(v).toLocaleDateString(t.locale, { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-');
    const row = (c, b) => { ensure(5); doc.setFont('helvetica', b ? 'bold' : 'normal'); doc.setFontSize(8.6); c.forEach((v, i) => doc.text(fitW(v, W[i]), X[i], y, R[i] ? { align: 'right' } : undefined)); y += 4.4; };
    const q = (v) => nf(v, Number.isInteger(+v) ? 0 : 1);
    row([P.sumArea, P.sumN, P.sumPeriod, P.sumPlan, P.sumIst], true);
    doc.setDrawColor(250, 204, 21); doc.line(M, y - 3.2, M + CW, y - 3.2);
    A.forEach((x) => {
      row([`${areaName(x)}${x.shared ? ' *' : ''}`, String(x.n), x.from ? `${d(x.from)} - ${d(x.to)}` : '-', x.cost ? eur(x.plan, t.locale) : '-', x.cost ? eur(x.ist, t.locale) : '-']);
      const sub = [`${P.sumTeams}: ${x.teams.join(', ') || '-'}`, x.qty.length ? `${P.sumQty}: ${x.qty.map((z) => `${z.name} ${q(z.ist)}${z.plan ? ` / ${q(z.plan)}` : ''} ${z.unit}`).join(' · ')}` : ''].filter(Boolean).join('   ');
      doc.setTextColor(90); para(sub, 7.6, M + 3, CW - 3); doc.setTextColor(20, 20, 20); y += 1.2;
    });
    doc.setTextColor(90); para([A.some((x) => x.shared) ? P.sumShared : '', P.sumNote].filter(Boolean).join(' '), 7.6); doc.setTextColor(20, 20, 20); y += 2;
  };
  const pitTable = (p) => {
    const c = computePit(p);
    ensure(16);
    doc.setFillColor(249, 115, 22); doc.rect(M, y - 3.2, 4, 4, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5);
    doc.text(fitW(`${p.name || t.pitTool} · ${t['pitShape_' + p.shape]} · ${f2(c.area, t.locale)} m² · ${t.pitDepth} ${f2(c.depth, t.locale)} · DN ${p.dn || '-'}`, CW - 7), M + 7, y); y += 5.5;
    const cols = [M + 2, M + 70, M + 88, M + 106, M + 148, M + CW];
    const row = (cells, bold) => {
      ensure(5); doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(8.5);
      const pw = [64, 16, 16, 16, 40, 30];
      cells.forEach((v, i) => doc.text(fitW(v, pw[i]), cols[i], y, i ? { align: 'right' } : undefined)); y += 4.6;
    };
    row([t.pitColLayer, t.pitColThick, 'm³', 't', t.pitColPrice, t.pitColCost], true);
    [...c.rows, c.excav].forEach((r) => row([layerName(t, r), r.key === 'excav' ? '' : Math.round(r.thick * 100), f2(r.m3, t.locale), f2(r.t, t.locale),
      r.price != null ? `${eur(r.price, t.locale)}/${unitLabel(r.unit).replace('³', '3').replace('²', '2')}` : '-', r.cost != null ? eur(r.cost, t.locale) : '-']));
    doc.setDrawColor(249, 115, 22); doc.line(cols[3], y - 3, cols[5], y - 3);
    row(['', '', '', '', t.pitTotal, eur(c.total, t.locale)], true);
    const extra = [p.note, memoText(p.memos)].filter(Boolean).join(' – ');
    if (extra) para(extra, 8.5, M + 2, CW - 2);
    const hits = pitClashes(p, project).filter(({ pipe }) => !p.lines?.[pipe.id]?.excluded);
    if (hits.length) {
      ensure(10); y += 2;
      doc.setFillColor(254, 226, 226); doc.rect(M, y - 4, CW, 6, 'F');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8.8); doc.setTextColor(185, 28, 28); doc.text(fitW(t.plTitle, CW - 4), M + 2, y); doc.setTextColor(20, 20, 20); y += 5;
      hits.forEach(({ pipe, d }) => {
        const r = p.lines?.[pipe.id] || {};
        const txt = [`${pipe.name} · ${t['type_' + pipe.type] || ''}${pipe.sub ? ' ' + t['sub_' + pipe.sub] : ''}`, r.depth ? `${t.plDepth} ${r.depth} m` : '', r.dist ? `${t.plDist} ${r.dist} m` : '', r.mat || pipe.material || '', r.dn || pipe.dn ? `DN ${r.dn || pipe.dn}` : '', t['plSt_' + (r.state || 'ok')], d ? `${d.toFixed(2)} m` : t.plCrossing].filter(Boolean).join(' · ');
        doc.setFont('helvetica', 'normal'); doc.setFontSize(8.3);
        const ls = doc.splitTextToSize(clean(txt), CW - 6); ensure(ls.length * 3.8); doc.text(ls, M + 4, y); y += ls.length * 3.8 + 0.8;
      });
    }
    y += 3;
  };
  const matTable = (data) => {
    const cols = [15, 100, 104, 152, 195];
    const al = [undefined, 'right', undefined, 'right', 'right'];
    const wd = [78, 16, 20, 30, 30];
    const row = (cells, bold) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(8.8);
      const nl = doc.splitTextToSize(clean(String(cells[0] ?? '')), wd[0]);
      ensure(nl.length * 4.2 + 1);
      cells.forEach((v, i) => doc.text(i ? fitW(v, wd[i]) : nl, cols[i], y, al[i] ? { align: al[i] } : undefined));
      y += nl.length * 4.2 + 0.8;
    };
    row([t.catName, t.colCount, t.colUnit, t.colUnitPrice, t.colAmount], true);
    data.rows.forEach((r) => row([`${r.label}${r.sku ? ' · ' + r.sku : ''}`, fmtQty(r, t.locale), t['unit_' + r.unit] || r.unit, r.price != null ? eur(+r.price + (+r.install || 0), t.locale) : '-', r.amount != null ? eur(r.amount, t.locale) : '-']));
    doc.setDrawColor(230, 81, 0); doc.line(cols[2], y - 3, cols[4], y - 3);
    vatBlock(data.total, cols[3], cols[4]); y += 3;
  };
  const vatBlock = (net, xl, xr) => {
    const v = vatSplit(net);
    [[t.vatNet, v.net, false], [fill(t.vatLine, { rate: v.rate.toLocaleString(t.locale) }), v.vat, false], [t.vatGross, v.gross, true]].forEach(([k, n, b]) => {
      ensure(5); doc.setFont('helvetica', b ? 'bold' : 'normal'); doc.setFontSize(b ? 9.5 : 8.8);
      doc.text(clean(k), xl - 30, y); doc.text(clean(eur(n, t.locale)), xr, y, { align: 'right' }); y += 4.8;
    });
  };
  const maintObjs = (rid) => [...fittings.map((o) => [o, t.objKind_fitting]), ...(project.pipes || []).map((o) => [o, t.objKind_pipe]), ...pits.map((o) => [o, t.objKind_pit])]
    .filter(([o]) => o.maint && (!rid || o.reportId === rid));
  const maintSection = (list) => {
    if (!list.length) return;
    section(t.mtTitle);
    list.forEach(([o]) => {
      ensure(10);
      doc.setFillColor(...hex(MAINT[o.maint.status])); doc.circle(M + 2, y - 1.3, 2, 'F');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5);
      const nl = doc.splitTextToSize(clean(`${o.name || '-'}${o.serial ? ' [' + o.serial + ']' : ''}`), 110 - 7);
      doc.text(nl, M + 7, y);
      doc.setFontSize(8.5);
      doc.text(fitW(t['mt_' + o.maint.status], 58), M + CW, y, { align: 'right' });
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7.8);
      doc.text(fitW(`${t.mtLast} ${fmt(o.maint.date)}`, 58), M + CW, y + 3.8, { align: 'right' });
      y += Math.max(nl.length * 4.6, 8) + 0.4;
      const rp = o.maint.repair;
      const extra = [rp ? fill(t.mtRepairedLine, { by: rp.by, date: fmt(rp.date), prev: t['mt_' + rp.prev] }) + (rp.note ? `: ${rp.note}` : '') : '',
        o.maint.tags?.length ? `${t.mtTags}: ${o.maint.tags.join(', ')}` : '', o.note, memoText(o.memos)].filter(Boolean).join(' – ');
      if (extra) para(extra, 8.5, M + 7, CW - 7);
      const ph = rp?.photos || {};
      const pics = ['before', 'after'].filter((w) => ph[w]);
      if (pics.length) {
        const pw = 55, phh = Math.min(42, pw * Math.max(...pics.map((w) => ph[w].h / ph[w].w)));
        ensure(phh + 8);
        pics.forEach((w, k) => {
          const x = M + 7 + k * (pw + 6), h = Math.min(phh, (pw * ph[w].h) / ph[w].w);
          doc.addImage(ph[w].data, 'JPEG', x, y, pw, h);
          doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(80, 80, 80);
          doc.text(clean(t[w === 'before' ? 'mtBefore' : 'mtAfter']), x, y + h + 3.5); doc.setTextColor(20, 20, 20);
        });
        y += phh + 6;
      }
      y += 1.5;
    });
    y += 2;
  };
  // all photos of components (fittings/pipes/pits): media, leak proof, repair before/after
  const objPhotos = (keep) => {
    const out = [];
    [[fittings, (o) => t['fit_' + o.type], 'fitting'], [project.pipes || [], (o) => t['type_' + o.type] || t.objKind_pipe, 'pipe'], [pits, () => t.objKind_pit, 'pit']].forEach(([list, typ, kind]) => list.filter(keep).forEach((o) => {
      const pt = objPos(o, kind, fittings);
      const base = { name: o.name || '-', type: typ(o) || '', coords: Array.isArray(pt) ? `${pt[0].toFixed(6)}, ${pt[1].toFixed(6)}` : '' };
      (o.media || []).forEach((m) => { const data = m.kind === 'video' ? m.thumb : m.data; if (data) out.push({ ...base, data, w: m.w, h: m.h, date: m.createdAt, cap: [m.leakProof ? t.pdfLeakProof : '', m.kind === 'video' ? t.video : '', m.caption].filter(Boolean).join(' · ') }); });
      [...(o.maintLog || []).filter((m) => m.repair), ...(o.maint?.repair && !(o.maintLog || []).some((m) => m.repair?.date === o.maint.repair.date) ? [o.maint] : [])].forEach((m) => ['before', 'after'].forEach((w) => {
        const ph = m.repair.photos?.[w];
        if (ph?.data) out.push({ ...base, data: ph.data, w: ph.w, h: ph.h, date: m.repair.date || m.date, cap: `${t.objPhRepair} – ${t[w === 'before' ? 'mtBefore' : 'mtAfter']}` });
      }));
    }));
    return out;
  };
  const photoGallery = (list) => {
    if (!list.length) return;
    section(t.objPhTitle);
    const pw = (CW - 6) / 2;
    for (let i = 0; i < list.length; i += 2) {
      const row = list.slice(i, i + 2), hs = row.map((p) => Math.min(70, (pw * (p.h || 3)) / (p.w || 4))), rh = Math.max(...hs);
      ensure(rh + 16);
      row.forEach((p, k) => {
        const x = M + k * (pw + 6);
        try { doc.addImage(p.data, 'JPEG', x, y, Math.min(pw, (hs[k] * (p.w || 4)) / (p.h || 3)), hs[k]); } catch { doc.setFillColor(230, 230, 230); doc.rect(x, y, pw, hs[k], 'F'); }
        doc.setFont('helvetica', 'bold'); doc.setFontSize(8.3); doc.setTextColor(20, 20, 20);
        doc.text(fitW(`${p.name} · ${p.type}`, pw), x, y + rh + 3.8);
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7.4); doc.setTextColor(90, 90, 90);
        doc.text(fitW([p.coords, p.date ? fmt(p.date) : ''].filter(Boolean).join(' · '), pw), x, y + rh + 7.2);
        if (p.cap) doc.text(fitW(p.cap, pw), x, y + rh + 10.4);
        doc.setTextColor(20, 20, 20);
      });
      y += rh + 14;
    }
    y += 2;
  };
  const endName = (pt) => (Array.isArray(pt) ? `${pt[0].toFixed(5)}, ${pt[1].toFixed(5)}` : fittings.find((f) => f.id === pt.fid)?.name || '?');

  const imagePage = async (c, item, title) => {
    newPage({ ...c, compact: true, top: 32 });
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(10, 37, 64);
    doc.text(doc.splitTextToSize(clean(title), CW)[0] || '', M, y); doc.setTextColor(20, 20, 20);
    y += 4;
    const capText = [item.caption, dirText(t, item), memoText(item.memos)].filter(Boolean).join(' – ');
    doc.setFontSize(9);
    const capLines = capText ? doc.splitTextToSize(clean(capText), CW).slice(0, 6) : [];
    const availH = PH - 20 - y - capLines.length * 4.4 - 4;
    const src = item.kind === 'video' ? item.thumb : item.data;
    const ratio = (item.h || 3) / (item.w || 4);
    let iw = CW, ih = CW * ratio;
    if (ih > availH) { ih = availH; iw = ih / ratio; }
    if (src) doc.addImage(src, 'JPEG', M + (CW - iw) / 2, y, iw, ih);
    else { doc.setFillColor(40, 50, 60); doc.rect(M, y, CW, ih, 'F'); }
    if (item.kind === 'video') {
      doc.setFillColor(198, 40, 40); doc.rect(M + (CW - iw) / 2, y, 34, 7, 'F');
      doc.setTextColor(255, 255, 255); doc.setFontSize(9); doc.text(`VIDEO ${fmtDur(item.duration)}`, M + (CW - iw) / 2 + 2, y + 5); doc.setTextColor(20, 20, 20);
    }
    y += ih + 5;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.text(capLines, M, y);
  };

  // ---- PAGE 1: PROJECT COVER ----
  const saved = fittings.map((f) => flowSaved(f.flowCheck)).filter((r) => r && r.ls > 0);
  const banner = () => {
    if (!saved.length) return;
    const s = saved.reduce((a, r) => ({ ls: a.ls + r.ls, m3a: a.m3a + r.m3a, eur: a.eur + r.eur }), { ls: 0, m3a: 0, eur: 0 });
    ensure(20);
    doc.setFillColor(22, 140, 60); doc.roundedRect(M, y - 4, CW, 17, 2, 2, 'F');
    doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
    doc.text(clean(`${t.fcBanner} (${saved.length})`), M + 4, y + 2);
    doc.setFontSize(9.5); doc.setFont('helvetica', 'normal');
    doc.text(clean(`${t.fcSaved}: ${s.ls.toFixed(2)} l/s · ${(s.ls * 86.4).toFixed(1)} m3/d · ${s.m3a.toFixed(0)} m3/a · ${s.eur.toFixed(0)} EUR/a`), M + 4, y + 8.5);
    doc.setTextColor(20, 20, 20); y += 18;
  };
  if (!isProject) banner();
  if (isProject) {
    setCtx(projCtx);
    const total = pipes.reduce((s, p) => s + pathLength(p.pts), 0);
    const ph = reports.reduce((a, r) => a + (r.photos || []).length, 0);
    const kpis = pipes.length || fittings.length || feat('network') || feat('fittings')
      ? [[t.reports, String(reports.length)], [t.fittingsTitle, String(fittings.length)], [t.pipesTitle, String(pipes.length)], [t.kpiLen, `${nf(total, 1)} m`]]
      : [[t.reports, String(reports.length)], [t.colPhotos, String(ph)], ...(project.scaffolds?.length ? [[t.cat_scaffold, String(project.scaffolds.length)]] : []), ...(project.drawings?.length ? [[t.dsTitle, String(project.drawings.length)]] : [])];
    const kw = CW / kpis.length;
    kpis.forEach(([k, v], i) => {
      doc.setFillColor(241, 245, 249); doc.roundedRect(M + i * kw, y - 4, kw - 3, 15, 2, 2, 'F');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(13, 71, 161);
      doc.text(v, M + i * kw + 3, y + 3.5);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(80, 80, 80);
      doc.text(doc.splitTextToSize(clean(k), kw - 8)[0], M + i * kw + 3, y + 8.5);
    });
    doc.setTextColor(20, 20, 20); y += 17;
    banner();
    if (project.address) { section(t.addrLabel); para(project.address, 9.5); y += 3; }
    if (project.description) { section(t.projDescTitle); para(project.description, 9.5); y += 3; }
    const all = [...(projectPt ? [[projectPt.lat, projectPt.lon]] : []), ...project.reports.map((r) => [r.lat, r.lon]), ...pipes.flatMap((p) => p.pts), ...(project.drawings || []).flatMap((d) => d.pts), ...fittings.map((f) => [f.lat, f.lon]), ...pits.flatMap(pitRing)];
    const SW = 900, SH = 700;
    const dims = allSurveys(project).flatMap(({ s, pt }) => surveyDims(s, pt));
    const snap = await mapSnapshot(overviewView(all, SW, SH), pipes, { fittings, netPipes: project.pipes, reports: project.reports, noOverlay: true, t, projectPt, pits, dims, drawings: project.drawings, camDirs: geoPhotos(reports) }, SW, SH);
    if (snap) { const h = Math.max(90, Math.min((CW * SH) / SW, PH - 32 - y)); const w = (h * SW) / SH; ensure(h); doc.addImage(snap, 'JPEG', M + (CW - w) / 2, y, w, h); y += h + 4; }
    legend();
    section(t.pdfToc);
    reports.forEach((r, i) => {
      ensure(6);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.text(`#${i + 1}`, M, y);
      doc.setFont('helvetica', 'normal');
      const meta = clean(`${r.lat.toFixed(6)}, ${r.lon.toFixed(6)}  |  ${r.photos.length} ${t.photos}`);
      doc.text(fitW(r.title, CW - 14 - doc.getTextWidth(meta)), M + 10, y);
      doc.text(meta, M + CW, y, { align: 'right' });
      y += 5.5;
    });
    areaTable();
    if (pipes.length) {
      y += 3; section(t.pdfPipes);
      pipes.forEach((pp) => {
        ensure(10);
        doc.setFillColor(...hex(pp.color)); doc.rect(M, y - 3.2, 4, 4, 'F');
        doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5);
        doc.text(fitW(`${pp.name} · ${t['type_' + pp.type] || pp.type || ''}${pp.dn && !String(t['type_' + pp.type] || '').includes(pp.dn) && !String(pp.name || '').includes(String(pp.dn)) ? ' · ' + pp.dn : ''}${pp.private ? ` · --- ${t.privPipe}` : ''}`, CW - 30), M + 7, y);
        doc.text(`${pp.private && pp.privLen ? nf(+String(pp.privLen).replace(',', '.'), 2) : nf(pathLength(pp.pts), 1)} m`, M + CW, y, { align: 'right' });
        y += 4.5; para(`${t.pdfRoute}: ${endName(pp.points[0])} > ${endName(pp.points[pp.points.length - 1])} (${pp.pts.length} ${t.drawPoints})${pp.depth != null && pp.depth !== '' ? ` · ${t.colDepthM}: ${nf(+String(pp.depth).replace(',', '.'), 2)}` : ''}${pp.material ? ` · ${pp.material}` : ''}${tooFlat(project, pp) ? ` · ! ${t.coverShort.replace('{m}', nf(tooFlat(project, pp), 2))}` : ''}`, 8.5, M + 7, CW - 7);
        y += 1.5;
      });
      ensure(7); doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
      doc.text(clean(`${t.pipeTotal}: ${nf(total, 1)} m`), M + CW, y, { align: 'right' }); y += 5;
      const priv = pipes.filter((p) => p.private), pubL = pipes.filter((p) => !p.private).reduce((s, p) => s + pathLength(p.pts), 0);
      const privL = priv.reduce((s, p) => s + (p.privLen ? +String(p.privLen).replace(',', '.') : pathLength(p.pts)), 0);
      const unk = fittings.filter((f) => f.privUnknown).length;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.8);
      para(`1. ${t.lenPublic}: ${nf(pubL, 1)} m   2. ${t.lenPrivate}: ${nf(privL, 1)} m${unk ? `   (!) ${unk}x ${t.privUnknownShort}` : ''}`, 8.8); y += 2;
    }
    if (fittings.length) {
      y += 3; section(t.pdfFittings);
      fittings.forEach((f, i) => {
        ensure(7);
        doc.setFillColor(...hex(FITTING_TYPES[f.type] || FITTING_TYPES.other)); doc.circle(M + 2, y - 1.3, 2, 'F');
        if (f.state && f.state !== 'none') { doc.setFillColor(...hex(FIT_STATES[f.state])); doc.circle(M + 3.6, y - 2.8, 0.9, 'F'); }
        doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5);
        const cat = [f.prod ? `${f.prod.name} ${f.prod.dim} - ${f.prod.mfr}` : f.cat?.name || t['fit_' + f.type], f.med ? t['med_' + f.med] : ''].filter(Boolean).join(' · ');
        const nm = `${i + 1}. ${f.name}${f.serial ? ' [' + f.serial + ']' : ''}${cat && !String(f.name).toLowerCase().includes(String(cat).toLowerCase()) ? ` (${cat})` : ''}`;
        const nl = doc.splitTextToSize(clean(nm), 120 - 7);
        doc.text(nl, M + 7, y);
        doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
        doc.text(f.gnss ? `${f.lat.toFixed(8)}, ${f.lon.toFixed(8)}` : `${f.lat.toFixed(6)}, ${f.lon.toFixed(6)}`, M + CW, y, { align: 'right' });
        const st = [f.state && f.state !== 'none' ? t['st_' + f.state] : '', f.type === 'leak' ? `${t.sevTitle}: ${t['sev_' + (f.severity || 'medium')]}` : '', f.note].filter(Boolean).join(' · ');
        const gl = [gnssLine(f.gnss, t), f.elev?.v != null ? `${t.altLbl}: ${(+f.elev.v).toFixed(2)} m (${t['altSrc_' + f.elev.src] || f.elev.src})` : '', surveyText(f.survey, t), f.depth != null && f.depth !== '' ? `${t.plDepth}: ${nf(+String(f.depth).replace(',', '.'), 2)} m` : '', redText(f, t), isRot(f) ? (() => { const a = fitArms(f, project.pipes, project.fittings); return t.armSum.replace('{a}', a.length).replace('{v}', a.filter((x) => armState(f, x.key) !== 'none').length).replace('{c}', a.filter((x) => armState(f, x.key) === 'closed').length); })() : '', f.privUnknown ? t.privUnknown : '', f.draft ? `!! ${t.prDraft}` : ''].filter(Boolean).join(' | ');
        y += nl.length * 4.4 + 0.6;
        if (st) { doc.setFontSize(8); doc.setTextColor(90); doc.text(doc.splitTextToSize(clean(st), CW - 7), M + 7, y); doc.setTextColor(20, 20, 20); y += 4.2; }
        if (gl) { doc.setFontSize(7.8); doc.setTextColor(21, 101, 192); const gls = doc.splitTextToSize(clean(gl), CW - 7); ensure(gls.length * 3.6); doc.text(gls, M + 7, y); doc.setTextColor(20, 20, 20); y += gls.length * 3.6 + 0.6; }
        const mt = memoText(f.memos);
        if (mt) { para(mt, 8.5, M + 7, CW - 7); y += 1; }
        const lp = f.type === 'leak' && (f.media || []).find((m) => m.kind === 'photo' && m.data);
        if (lp) {
          const lw = 48, lh = Math.min(40, (lw * (lp.h || 3)) / (lp.w || 4));
          ensure(lh + 6); doc.addImage(lp.data, 'JPEG', M + 7, y, lw, lh);
          doc.setFontSize(7.5); doc.setTextColor(198, 40, 40); doc.text(clean(t.leakPhotoCap), M + 7, y + lh + 3.5); doc.setTextColor(20, 20, 20);
          y += lh + 7;
        }
      });
    }
  }

  if (isProject) maintSection(maintObjs());
  const open = isProject ? maintObjs().filter(([o]) => o.maint.status !== 'ok').sort((a, b) => (b[0].maint.status === 'defect') - (a[0].maint.status === 'defect')) : [];
  if (open.length) {
    newPage(projCtx);
    section(t.mtOverview);
    const marks = open.map(([o, kind], i) => ({ pt: objPos(o, kind === t.objKind_pipe ? 'pipe' : kind === t.objKind_pit ? 'pit' : 'fitting', project.fittings), n: i + 1, color: MAINT[o.maint.status] })).filter((m) => m.pt);
    const SW = 900, SH = 640;
    const snap = await mapSnapshot(overviewView(marks.map((m) => m.pt), SW, SH), pipes, { pits, markers: marks, noOverlay: true, noCross: true, t }, SW, SH);
    if (snap) { const h = (CW * SH) / SW; ensure(h); doc.addImage(snap, 'JPEG', M, y, CW, h); y += h + 4; }
    ['defect', 'warn'].forEach((k) => { doc.setFillColor(...hex(MAINT[k])); doc.circle(M + 2 + (k === 'warn' ? 70 : 0), y - 1.2, 2, 'F'); doc.setFontSize(8.5); doc.setFont('helvetica', 'normal'); doc.text(clean(t['mt_' + k]), M + 6 + (k === 'warn' ? 70 : 0), y); });
    y += 6;
    open.forEach(([o], i) => {
      ensure(6);
      doc.setFillColor(...hex(MAINT[o.maint.status])); doc.circle(M + 2.5, y - 1.3, 2.6, 'F');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(o.maint.status === 'warn' ? 20 : 255); doc.text(String(i + 1), M + 2.5, y - 0.3, { align: 'center' });
      doc.setTextColor(20, 20, 20); doc.setFontSize(9); doc.text(fitW(o.name || '-', CW * 0.5), M + 8, y);
      doc.setFont('helvetica', 'normal'); doc.text(fitW((o.maint.tags || []).join(', ') || t['mt_' + o.maint.status], CW * 0.45), M + CW, y, { align: 'right' });
      y += 5.5;
    });
  }
  const dmg = isProject ? damagePoints(project.fittings, project.pipes, pits) : [];
  if (dmg.length) {
    newPage(projCtx);
    section(t.heatPdfTitle);
    para(t.heatPdfHint, 8.5); y += 1;
    const SW = 900, SH = 640;
    const snap = await mapSnapshot(overviewView(dmg.map((d) => [d[0], d[1]]), SW, SH), pipes, { pits, heat: dmg, noOverlay: true, noCross: true, t }, SW, SH);
    if (snap) { const h = (CW * SH) / SW; ensure(h); doc.addImage(snap, 'JPEG', M, y, CW, h); y += h + 3; }
    [['#fde047', t.heatLow], ['#f97316', ''], ['#dc2626', ''], ['#7f1d1d', t.heatHigh]].forEach(([c, l], i) => { doc.setFillColor(...hex(c)); doc.rect(M + i * 12, y - 3, 12, 3.5, 'F'); if (l) { doc.setFontSize(7.5); doc.text(clean(l), M + i * 12 + (i ? 12 : 0), y + 4, i ? { align: 'right' } : undefined); } });
    y += 9;
    const leaks = fittings.filter((f) => f.type === 'leak');
    leaks.forEach((f, i) => {
      ensure(6);
      doc.setFillColor(...hex(FITTING_TYPES.leak)); doc.circle(M + 2, y - 1.3, 2, 'F');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.text(fitW(`${i + 1}. ${f.name}`, 70), M + 7, y);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
      doc.text(fitW(`${t.sevTitle}: ${t['sev_' + (f.severity || 'medium')]}`, 40), M + 80, y);
      const hasPh = (f.media || []).some((m) => m.kind === 'photo');
      doc.text(clean(`${f.lat.toFixed(f.gnss ? 8 : 6)}, ${f.lon.toFixed(f.gnss ? 8 : 6)}${hasPh ? '  |  ' + t.leakPhotoCap : ''}`), M + CW, y, { align: 'right' });
      y += 5;
    });
  }
  // longitudinal profiles of GNSS-surveyed pipes
  const profs = isProject ? (project.pipes || []).map((p) => [p, profileData(p.profile)]).filter(([, d]) => d) : [];
  profs.forEach(([p, d]) => {
    newPage(projCtx);
    section(`${t.glProfile} – ${p.name}`);
    const X0 = M + 14, X1 = M + CW - 2, Y0 = y + 2, Y1 = y + 72;
    const px = (st) => X0 + (st / (d.len || 1)) * (X1 - X0), py = (h) => Y1 - ((h - d.min) / (d.max - d.min)) * (Y1 - Y0);
    doc.setDrawColor(148, 163, 184); doc.setLineWidth(0.25); doc.rect(X0, Y0, X1 - X0, Y1 - Y0);
    doc.setFontSize(7); doc.setTextColor(71, 85, 105);
    for (let k = 0; k <= 4; k++) { const h = d.min + ((d.max - d.min) * k) / 4; doc.text(h.toFixed(2), X0 - 1.5, py(h) + 1, { align: 'right' }); doc.line(X0, py(h), X0 + 1.5, py(h)); }
    for (let k = 0; k <= 5; k++) { const st = (d.len * k) / 5; doc.text(`${st.toFixed(1)} m`, px(st), Y1 + 4, { align: 'center' }); }
    const draw = (k, rgb, dash) => {
      const q = d.pts.filter((v) => v[k] != null);
      doc.setDrawColor(...rgb); doc.setLineWidth(0.7);
      if (dash) doc.setLineDashPattern([1.6, 1], 0);
      q.forEach((v, i) => i && doc.line(px(q[i - 1].st), py(q[i - 1][k]), px(v.st), py(v[k])));
      doc.setLineDashPattern([], 0);
    };
    draw('ground', [146, 64, 14]); draw('pipe', [21, 101, 192], true);
    doc.setTextColor(20, 20, 20); y = Y1 + 9;
    doc.setFontSize(8); doc.setFillColor(146, 64, 14); doc.rect(M, y - 2.5, 6, 1.6, 'F'); doc.text(clean(t.gnssGround), M + 8, y);
    doc.setFillColor(21, 101, 192); doc.rect(M + 60, y - 2.5, 6, 1.6, 'F'); doc.text(clean(t.gnssPipe), M + 68, y);
    doc.text(clean(`${t.glLength}: ${d.len.toFixed(2)} m · ${d.pts.length} ${t.glPoints}`), M + CW, y, { align: 'right' }); y += 7;
    const cols = [M, M + 10, M + 32, M + 62, M + 92, M + 114, M + 142];
    const row = (cells, bold) => { ensure(5); doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(8); cells.forEach((v, i) => doc.text(fitW(String(v), (cols[i + 1] || M + CW) - cols[i] - 1.5), cols[i], y)); y += 4.4; };
    row(['#', t.glStation, `${t.gnssGround} (m)`, `${t.gnssPipe} (m)`, t.glDepth, 'Fix / ±', t.gnssCoords], true);
    d.pts.forEach((v, i) => row([i + 1, `${v.st.toFixed(2)} m`, v.ground?.toFixed(3) ?? '–', v.pipe?.toFixed(3) ?? '–', v.depth != null ? `${v.depth.toFixed(2)} m` : '–', `${FIX_SHORT[v.fix] || '–'} ${v.acc != null ? '± ' + v.acc.toFixed(3) : ''}`, `${v.lat.toFixed(8)}, ${v.lon.toFixed(8)}`]));
  });
  // noise logger protocol (incl. collected / moved)
  const logs = isProject ? project.loggers || [] : [];
  if (logs.length) {
    newPage(projCtx);
    section(t.lgProtocol);
    const fd = (ms) => (ms ? new Date(ms).toLocaleString(t.locale, { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '–');
    const cols = [M, M + 52, M + 80, M + 106, M + 132, M + 150];
    const row = (cells, bold, rgb) => { ensure(5); doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(8); if (rgb) doc.setTextColor(...rgb); cells.forEach((v, i) => doc.text(fitW(String(v), (cols[i + 1] || M + CW) - cols[i] - 1.5), cols[i], y)); doc.setTextColor(20, 20, 20); y += 4.6; };
    row([t.lgName, t.lgModeCol, t.lgPlacedAt, t.lgRemoved, t.lgLying, t.gnssCoords], true);
    const findRow = (f) => { if (!f || (f.noise == null && !f.note)) return; ensure(5); doc.setFont('helvetica', f.noise ? 'bold' : 'normal'); doc.setFontSize(8); doc.setTextColor(...(f.noise ? [185, 28, 28] : [71, 85, 105])); doc.text(fitW(`   ${t.lcFinding}: ${[f.noise == null ? '' : f.noise ? t.lcNoise : t.lcQuiet, f.note].filter(Boolean).join(' – ')}`, CW), M, y); doc.setTextColor(20, 20, 20); y += 4.6; };
    logs.forEach((l) => {
      (l.history || []).forEach((h) => { row([`${l.name} (${t.lgMovedShort})`, t['lgMode_' + l.mode], fd(h.placedAt), fd(h.removedAt), fmtDuration(h.removedAt - h.placedAt, t), `${h.lat.toFixed(6)}, ${h.lon.toFixed(6)}`], false, [100, 116, 139]); findRow(h.finding); });
      const end = l.collectedAt || l.missingAt || Date.now();
      row([l.name, t['lgMode_' + l.mode], fd(l.placedAt), l.collectedAt ? fd(l.collectedAt) : l.missingAt ? `${t.lgSt_missing} ${fd(l.missingAt)}` : t['lgSt_' + loggerState(l)], fmtDuration(end - l.placedAt, t), `${l.lat.toFixed(6)}, ${l.lon.toFixed(6)}`]);
      findRow(l.finding);
    });
  }
  // noise spots: where to correlate (red points on map + list)
  const nsp = isProject ? noiseSpots(project) : [];
  if (nsp.length) {
    newPage(projCtx);
    section(t.nsPdfTitle);
    para(t.nsPdfHint, 8.5); y += 1;
    const SW = 900, SH = 640, marks = nsp.map((s, i) => ({ pt: [s.lat, s.lon], n: i + 1, color: '#dc2626' }));
    const snap = await mapSnapshot(overviewView(marks.map((m) => m.pt), SW, SH), pipes, { pits, markers: marks, noOverlay: true, noCross: true, t }, SW, SH);
    if (snap) { const h = (CW * SH) / SW; ensure(h); doc.addImage(snap, 'JPEG', M, y, CW, h); y += h + 4; }
    nsp.forEach((s, i) => {
      ensure(s.note ? 10 : 6);
      doc.setFillColor(220, 38, 38); doc.circle(M + 2.5, y - 1.3, 2.6, 'F');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(255); doc.text(String(i + 1), M + 2.5, y - 0.3, { align: 'center' });
      doc.setTextColor(20, 20, 20); doc.setFontSize(9); doc.text(fitW(`${s.name}${s.id.endsWith('-c') ? '' : ` (${t.nsOld})`}`, CW * 0.45), M + 8, y);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.text(fitW(`${new Date(s.at).toLocaleString(t.locale, { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })} · ${s.lat.toFixed(6)}, ${s.lon.toFixed(6)}`, CW * 0.5), M + CW, y, { align: 'right' });
      y += 4.6;
      if (s.note) { doc.setFontSize(8); doc.setTextColor(71, 85, 105); doc.text(fitW(s.note, CW - 8), M + 8, y); doc.setTextColor(20, 20, 20); y += 4.4; }
      y += 1;
    });
  }
  // gas / cable / microduct data of pipes
  const utl = (project.pipes || []).map((p) => [p, pipeUtilText(p, t, pathLength(pipeLatLngs(p, fittings)))]).filter(([, s]) => s);
  if (utl.length) {
    if (isProject) newPage(projCtx); else { ensure(30); y += 4; }
    section(t.utPdfTitle);
    utl.forEach(([p, s]) => { ensure(10); doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.text(fitW(`${p.name || t.objKind_pipe} (${t['type_' + p.type] || p.type}${p.dn ? ` DN ${p.dn}` : ''})`, CW), M, y); y += 4.6; para(s.split(' | ').join('\n'), 8.5, M + 3, CW - 3); y += 2; });
  }
  // hydrant flow & flushing protocols
  const hyds = fittings.filter((f) => f.hydLogs?.length);
  if (hyds.length) {
    if (isProject) newPage(projCtx); else { ensure(30); y += 4; }
    section(t.hyPdfTitle);
    const cols = [M, M + 26, M + 46, M + 66, M + 90, M + 106, M + 126, M + 150];
    const row = (cells, bold, rgb) => { ensure(5); doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(8); cells.forEach((v, i) => { if (rgb && i === cells.length - 1) doc.setTextColor(...rgb); doc.text(fitW(String(v), (cols[i + 1] || M + CW) - cols[i] - 1.5), cols[i], y); doc.setTextColor(20, 20, 20); }); y += 4.6; };
    for (const f of hyds) {
      ensure(18); doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.text(fitW(`${f.name || t.type_hydrant || 'Hydrant'}${f.serial ? ` · ${f.serial}` : ''} · ${f.lat?.toFixed(6)}, ${f.lon?.toFixed(6)}`, CW), M, y); y += 5;
      row([t.hyDate, t.hyPs, t.hyPf, t.hyQ, t.hyDur, t.hyVol, t.hyClar, t.hyRes], true);
      const ls = [...f.hydLogs].sort((a, b) => a.t - b.t);
      ls.forEach((e) => row(hydRow(e, t), false, e.res === 'pass' ? [22, 163, 74] : e.res === 'fail' ? [220, 38, 38] : null));
      const phs = ls.filter((e) => e.photo);
      if (phs.length) { ensure(40); phs.slice(0, 4).forEach((e, i) => doc.addImage(e.photo, 'JPEG', M + i * 45, y, 42, 32)); y += 35; }
      para(t.hyNorm.replace('{q}', HYD_MIN_Q).replace('{p}', '1,5'), 7.5); y += 3;
    }
  }
  // gas network inspection & leak protocol
  const gls = gasLeaks(project), kps = kksPoints(project);
  if (gls.length || kps.length) {
    if (isProject) newPage(projCtx); else { ensure(30); y += 4; }
    section(t.glPdfTitle);
    if (kps.length) {
      const kc = [M, M + 8, M + 40, M + 60, M + 80, M + 104, M + 132];
      const kr = (cells, bold, rgb) => { ensure(5); doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(7.8); cells.forEach((v, i) => { if (rgb && i === 4) doc.setTextColor(...rgb); doc.text(fitW(String(v), (kc[i + 1] || M + CW) - kc[i] - 1.5), kc[i], y); doc.setTextColor(20, 20, 20); }); y += 4.6; };
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.text(t.kkTitle, M, y); y += 5;
      kr(kksHead(t).slice(0, 7), true);
      kps.forEach((f, i) => { const ok = kksOk(f.kks); kr(kksRow(f, t, i).slice(0, 7), false, ok == null ? null : ok ? [22, 163, 74] : [220, 38, 38]); });
      const kch = kps.filter((f) => (f.kks?.hist || []).length > 1);
      kch.forEach((f) => {
        const W = 90, H = 26, c = kksChart(f.kks, W, H); ensure(H + 10);
        doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.text(fitW(`${f.name || t.fit_kks}: ${t.kkHist} (${f.kks.hist.length})${kksTrend(f.kks) === 'down' ? ` - ${t.kkTrDown}` : ''}`, CW), M, y); y += 2;
        doc.setDrawColor(203, 213, 225); doc.rect(M, y, W, H);
        doc.setDrawColor(220, 38, 38); doc.setLineDashPattern([1.5, 1], 0); doc.line(M, y + c.lim, M + W, y + c.lim); doc.setLineDashPattern([], 0);
        doc.setDrawColor(13, 148, 136); doc.setLineWidth(0.5); c.off.slice(1).forEach((p, i) => doc.line(M + c.off[i][0], y + c.off[i][1], M + p[0], y + p[1])); doc.setLineWidth(0.2);
        c.off.forEach((p) => { doc.setFillColor(...(p[2] <= -850 ? [22, 163, 74] : [220, 38, 38])); doc.circle(M + p[0], y + p[1], 0.9, 'F'); });
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.text(kksHist(f.kks).map((x) => `${new Date(x.t).toLocaleDateString(t.locale)}: ${x.eoff}${x.eon != null ? `/${x.eon}` : ''} mV`).slice(-6).join('\n'), M + W + 4, y + 3);
        y += H + 5;
      });
      const kph = kps.filter((f) => f.kks?.photo);
      if (kph.length) { ensure(36); kph.slice(0, 4).forEach((f, i) => doc.addImage(f.kks.photo, 'JPEG', M + i * 45, y, 42, 32)); y += 35; }
      y += 3;
    }
    if (gls.length) {
    const cols = [M, M + 8, M + 44, M + 80, M + 96, M + 112, M + 126];
    const CLR = { A: [220, 38, 38], B: [202, 138, 4], C: [22, 163, 74] };
    const row = (cells, bold, rgb) => { ensure(5); doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(7.8); cells.forEach((v, i) => { if (rgb && i === 2) doc.setTextColor(...rgb); doc.text(fitW(String(v), (cols[i + 1] || M + CW) - cols[i] - 1.5), cols[i], y); doc.setTextColor(20, 20, 20); }); y += 4.6; };
    const h = gasLeakHead(t);
    row([h[0], h[1], h[2], h[3], h[4], h[5], h[6]], true);
    gls.forEach((f, i) => { const r = gasLeakRow(f, t, i); row(r.slice(0, 7), false, CLR[f.gl.cls]); para(`${r[9]}, ${r[10]}${r[7] ? ` · ${r[7]}` : ''}`, 7.2, M + 8, CW - 8); y += 0.6; });
    }
  }
  // correlation results + cable fault search zones
  const cors = fittings.filter((f) => f.corr);
  const flts = project.faults || [], cfp = cfPins(project);
  if (cors.length || flts.length || cfp.length) {
    if (isProject) newPage(projCtx); else { ensure(30); y += 4; }
    if (cors.length) {
      section(t.ccTitle);
      cors.forEach((f) => { const c = f.corr; ensure(12); doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.text(fitW(`${f.name}: ${c.aName} - ${c.bName} · L = ${c.L.toFixed(1)} m`, CW), M, y); y += 4.5; para(`${c.mode === 'dist' ? `${t.ccDistA} ${c.val} m` : `dt ${c.val} ms`} -> ${c.x.toFixed(2)} m ${t.ccFromA} | ${c.secs.map((s) => `${s.name} ${s.mat || ''} DN${s.dn || '-'} ${s.len.toFixed(1)} m @ ${s.v} m/s`).join('; ')}`, 7.8, M + 3, CW - 3); y += 2; });
    }
    if (flts.length) {
      section(t.cfTitle);
      flts.forEach((x) => { ensure(8); para(`${new Date(x.t).toLocaleString(t.locale)} · ${x.startName}: ${t.cfDist} ${x.d} m, ${t.cfSlack} ${x.slack} % -> ${t.cfGround} ${x.g.toFixed(2)} m +/- ${x.tol.toFixed(2)} m · ${x.pins.map((p) => `${p[0].toFixed(7)}, ${p[1].toFixed(7)}`).join(' | ')}${x.note ? ` · ${x.note}` : ''}`, 8.2); y += 1.5; });
    }
    if (cfp.length) {
      section(t.cfPinTitle);
      cfp.forEach((f) => { ensure(10); doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.text(fitW(`${f.name || t.fit_cfault} · ${f.lat.toFixed(7)}, ${f.lon.toFixed(7)}`, CW), M, y); y += 4.5; para((cfText(f, t, flts) || '-').replace(/Ω/g, 'Ohm'), 8, M + 3, CW - 3); y += 1.5; });
    }
  }
  // line locating, tracer gas & ground microphone points
  const lps = locPoints(project), trs = fittings.filter((f) => f.type === 'tracer'), gms = fittings.filter((f) => f.type === 'gmic');
  if (lps.length || trs.length || gms.length) {
    if (isProject) newPage(projCtx); else { ensure(30); y += 4; }
    if (lps.length) {
      section(t.loTitle);
      lps.forEach((f) => { ensure(9); para(`${f.name || t.fit_locate} (${f.lat.toFixed(7)}, ${f.lon.toFixed(7)}): ${locText(f, t) || '-'}`, 8.2); y += 1; });
      const lph = lps.filter((f) => f.loc?.photo);
      if (lph.length) { ensure(36); lph.slice(0, 4).forEach((f, i) => doc.addImage(f.loc.photo, 'JPEG', M + i * 45, y, 42, 32)); y += 35; }
    }
    if (trs.length || gms.length) {
      section(t.trgPdfTitle);
      trs.forEach((f) => { ensure(8); para(`💨 ${f.name || t.fit_tracer}: ${t['trgGas_' + (f.tr?.gas || 'h2n2')]}${f.tr?.bar ? `, ${f.tr.bar} bar` : ''}${f.tr?.start ? `, ${t.trgStart} ${f.tr.start.replace('T', ' ')}` : ''}${f.tr?.end ? `, ${t.trgEnd} ${f.tr.end.replace('T', ' ')}` : ''}${f.tr?.qty ? `, ${t.trgQty} ${f.tr.qty} ${f.tr.unit === 'l' ? 'l' : f.tr.unit === 'kg' ? 'kg' : 'm³'}` : ''}${f.tr?.bottles ? `, ${f.tr.bottles}× ${t.trgBottles}` : ''} (${f.lat.toFixed(7)}, ${f.lon.toFixed(7)})`.replace('💨 ', ''), 8.2); y += 1; });
      [...gms].sort((a, b) => (Number(b.gm?.int) || 0) - (Number(a.gm?.int) || 0)).forEach((f, i) => { ensure(7); if (!i) doc.setTextColor(220, 38, 38); para(`${f.name || t.fit_gmic}: ${t.gmInt} ${f.gm?.int ?? '-'} %${f.gm?.ppm ? ` · ${f.gm.ppm} ppm H2` : ''} (${f.lat.toFixed(7)}, ${f.lon.toFixed(7)})${!i ? ` - ${t.gmMax}` : ''}`, 8.2); doc.setTextColor(20, 20, 20); y += 0.6; });
    }
  }
  // sewer inspection (damage per reach) + in-house leak protocol
  const reaches = (project.pipes || []).filter((p) => p.insp?.items?.length), bLeaks = fittings.filter((f) => bldHas(f.bld));
  if (reaches.length || bLeaks.length) {
    if (isProject) newPage(projCtx); else { ensure(30); y += 4; }
    if (reaches.length) {
      section(t.siTitle);
      reaches.forEach((p) => {
        ensure(10); doc.setFont('helvetica', 'bold'); doc.setFontSize(9); const hl = doc.splitTextToSize(clean(reachHead(p, t, endName)), CW).slice(0, 2); doc.text(hl, M, y); y += 4.5 * hl.length;
        const hk = hkLine(p, t); if (hk) { doc.setFont('helvetica', 'normal'); doc.setFontSize(8); const hh = doc.splitTextToSize(clean(hk), CW).slice(0, 3); ensure(4 * hh.length); doc.text(hh, M, y); y += 4 * hh.length; doc.setFont('helvetica', 'bold'); doc.setFontSize(9); }
        if (p.dp) { const ok = dpPass(p.dp); doc.setFontSize(8); doc.text(clean(`${t.dtTitle}: ${p.dp.method ? t['dpM_' + p.dp.method] : ''} · ${ok == null ? t.dpOpen : ok ? t.dpOk : t.dpFail}`), M, y); y += 4.5; doc.setFontSize(9); }
        inspItems(p.insp).forEach((d) => { doc.setTextColor(...(CLS_RGB[d.cls] || [20, 20, 20])); para(dmgLine(d, t), 8, M + 3, CW - 3); doc.setTextColor(20, 20, 20); });
        y += 1.5;
      });
    }
    if (bLeaks.length) {
      section(t.bpTitle);
      bLeaks.forEach((f) => { ensure(9); para(`${f.name || t.fit_leak} (${f.lat.toFixed(7)}, ${f.lon.toFixed(7)}): ${bldText(f.bld, t)}`, 8.2); y += 1; });
    }
  }
  // Betriebstagebuch / Follow-Me protocol (mode-specific tracks)
  const lrs = logRows(project.tracks || [], t);
  if (lrs.length) {
    ensure(30); y += 4;
    section(t.tlTitle);
    const h = logHead(t);
    lrs.forEach((r) => {
      ensure(12); doc.setFont('helvetica', 'bold'); doc.setFontSize(8.8);
      doc.text(fitW([r[0], r[1], r[2], r[3], r[4], r[5]].filter(Boolean).join(' · '), CW), M, y); y += 4.4;
      para([r[6] && `${h[6]}: ${r[6]}`, `${r[7]} km`, `Ø ${r[8]} km/h`, `max ${r[9]} km/h`, r[10] ? `${h[10]}: ${r[10]}` : '', r[11]].filter(Boolean).join(' · '), 8, M + 3, CW - 3); y += 1.5;
    });
  }
  // saved compass cross-bearings
  const xbs = xbPoints(project);
  if (xbs.length) {
    ensure(30); y += 4;
    section(t.xbTitle);
    xbs.forEach((f) => { ensure(10); doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.text(fitW(`${f.name} · ${f.lat.toFixed(7)}, ${f.lon.toFixed(7)}${f.trackRef ? ` · ${f.trackRef}` : ''}`, CW), M, y); y += 4.5; para(xbText(f, t), 8, M + 3, CW - 3); y += 1.5; if (f.xb.photo) { ensure(46); doc.addImage(f.xb.photo, 'JPEG', M + 3, y, 56, 42); y += 45; } });
  }
  // scaffold rental
  const scs = project.scaffolds || [];
  if (scs.length) {
    ensure(30); y += 4;
    section(t.scTitle);
    scs.forEach((x) => { const r = scafState(x); if (r.st === 'none') return; ensure(8); if (r.st === 'red' || r.st === 'doneOver') doc.setTextColor(220, 38, 38); para(`${x.name}: ${t.scStart} ${new Date(x.start).toLocaleDateString(t.locale)}, ${x.weeks} ${t.scWeeksShort} -> ${t.scDue} ${new Date(r.due).toLocaleDateString(t.locale)}${x.end ? `, ${t.scEnd} ${new Date(x.end).toLocaleDateString(t.locale)}` : ''} · ${t['scSt_' + r.st]} · ${t.scBase} ${nf(r.base, 2)} ${cur()}${r.extra ? ` · ${t.scExtra} ${r.extraW} x = ${nf(r.extra, 2)} ${cur()}` : ''}`, 8.4); doc.setTextColor(20, 20, 20); y += 1; });
  }
  // hand sketches of building surveys (to scale)
  const svys = isProject ? allSurveys(project) : [];
  if (svys.length) {
    newPage(projCtx);
    section(t.svySketchTitle);
    para(t.svySketchHint, 8.5); y += 2;
    const BW = (CW - 6) / 2, BH = 62;
    svys.forEach(({ s, pt, name }, n) => {
      const col = n % 2;
      if (!col) ensure(BH + 12);
      const x0 = M + col * (BW + 6), y0 = y;
      doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.3); doc.rect(x0, y0, BW, BH + 8);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(20, 20, 20); doc.text(fitW(`${n + 1}. ${name || ''}`, BW - 4), x0 + 2, y0 + 4.5);
      const dims = surveyDims(s, pt);
      const P = [s.a, s.b, pt, ...dims.flatMap((d) => [d.p, d.q])].filter(Boolean).map((q) => offsetM(s.a, q));
      const xs = P.map((q) => q[0]), ys = P.map((q) => q[1]);
      const rx = Math.max(1, Math.max(...xs) - Math.min(...xs)), ry = Math.max(1, Math.max(...ys) - Math.min(...ys));
      const sc = Math.min((BW - 16) / rx, (BH - 14) / ry);
      const cx = (Math.max(...xs) + Math.min(...xs)) / 2, cy = (Math.max(...ys) + Math.min(...ys)) / 2;
      const X = (q) => x0 + BW / 2 + (offsetM(s.a, q)[0] - cx) * sc, Y = (q) => y0 + 8 + (BH - 6) / 2 - (offsetM(s.a, q)[1] - cy) * sc;
      if (s.b) { doc.setDrawColor(100, 116, 139); doc.setLineWidth(2.2); doc.line(X(s.a), Y(s.a), X(s.b), Y(s.b)); }
      doc.setLineWidth(0.45); doc.setDrawColor(15, 23, 42); doc.setLineDashPattern([1.4, 1], 0);
      dims.filter((d) => !d.base).forEach((d) => doc.line(X(d.p), Y(d.p), X(d.q), Y(d.q)));
      doc.setLineDashPattern([], 0);
      doc.setFontSize(7.5);
      dims.filter((d) => d.label).forEach((d) => {
        const mx = (X(d.p) + X(d.q)) / 2, my = (Y(d.p) + Y(d.q)) / 2, w = doc.getTextWidth(d.label) + 2;
        doc.setFillColor(250, 204, 21); doc.rect(mx - w / 2, my - 2.4, w, 3.6, 'F'); doc.setTextColor(15, 23, 42); doc.text(d.label, mx, my + 0.4, { align: 'center' });
      });
      [[s.a, s.labelA || 'A'], [s.b, s.labelB || 'B']].forEach(([c, l]) => { if (!c) return; doc.setFillColor(15, 23, 42); doc.rect(X(c) - 1.3, Y(c) - 1.3, 2.6, 2.6, 'F'); doc.setTextColor(71, 85, 105); doc.text(fitW(l, 30), X(c) + 2, Y(c) - 2); });
      doc.setFillColor(...hex(FITTING_TYPES.valve)); doc.circle(X(pt), Y(pt), 1.6, 'F');
      doc.setFontSize(6.8); doc.setTextColor(71, 85, 105); doc.text(fitW(surveyText(s, t), BW - 4), x0 + 2, y0 + BH + 6.5);
      doc.setTextColor(20, 20, 20);
      if (col || n === svys.length - 1) y += BH + 12;
    });
    const sp = svys.flatMap(({ s, name }) => (s.photos || []).map((p) => ({ p, name })));
    if (sp.length) {
      ensure(30); section(t.svpPdfTitle);
      const PW = (CW - 6) / 2, PH2 = 72;
      sp.forEach(({ p, name }, n) => {
        const col = n % 2;
        if (!col) ensure(PH2 + 14);
        const k = Math.min(PW / (p.w || PW), PH2 / (p.h || PH2)), iw = (p.w || PW) * k, ih = (p.h || PH2) * k, x0 = M + col * (PW + 6);
        try { doc.addImage(p.data, 'JPEG', x0 + (PW - iw) / 2, y, iw, ih); } catch { /* skip broken image */ }
        doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(20, 20, 20); doc.text(fitW(name || '', PW), x0, y + PH2 + 4);
        doc.setFont('helvetica', 'normal'); doc.setFontSize(6.8); doc.setTextColor(71, 85, 105); doc.text(fitW((p.caption || '').replace(/─/g, '-').replace(/►/g, '>'), PW), x0, y + PH2 + 8);
        doc.setTextColor(20, 20, 20);
        if (col || n === sp.length - 1) y += PH2 + 13;
      });
    }
  }
  const pMat = isProject && getMode() !== 'survey' ? materialRows(project) : { rows: [] };
  if (pMat.rows.length) { newPage(projCtx); section(`${t.matList} – ${t.matProject} – ${t.dsExecQty}`); matTable(pMat); }
  const planSections = () => {
  const plMat = materialRows(project, null, true);
  if (plMat.rows.length && getMode() !== 'survey') { if (isProject) newPage(projCtx); else { ensure(30); y += 4; } section(`${t.matList} – ${t.dsPlanQty}`); matTable(plMat); }
  const dsum = drawSummary(project.drawings);
  if (dsum.length) {
    if (isProject) newPage(projCtx); else { ensure(30); y += 4; }
    section(t.dsTitle);
    doc.setFontSize(8.5);
    const row = (c, bold) => { ensure(10); doc.setFont('helvetica', bold ? 'bold' : 'normal'); const ls = c.map((v, i) => doc.splitTextToSize(clean(String(v)), i ? 33 : 66)); [M, M + 70, M + 105, M + 140].forEach((x, i) => doc.text(ls[i], x, y)); y += 4 * Math.max(...ls.map((l) => l.length)) + 1; };
    row([t.dsLegend, t.dsPlanQty, t.dsExecQty, 'n'], true);
    dsum.forEach((r) => { const u = r.kind === 'area' ? 'm²' : 'm'; ensure(6); if (r.kind === 'area') { doc.setFillColor(...hex(PATTERNS[r.style] || '#cbd5e1')); doc.rect(M, y - 3, 4, 3.5, 'F'); } else { doc.setDrawColor(...hex(LINE_STYLES[r.style] || '#0f172a')); doc.setLineWidth(r.style === 'wall' ? 1.2 : 0.6); doc.line(M, y - 1.3, M + 4, y - 1.3); doc.setLineWidth(0.2); } doc.text(clean(t['dsSt_' + r.style] || r.style), M + 6, y); [[M + 70, r.plan], [M + 105, r.built]].forEach(([x, v]) => doc.text(`${nf(v, 2)} ${u}`, x, y)); doc.text(String(r.n), M + 140, y); y += 5; });
    (project.drawings || []).filter((d) => d.actual?.note || d.actual?.qty || d.actual?.h).forEach((d) => doc.splitTextToSize(clean(`${t['dsSt_' + d.style] || d.text || d.kind}: ${t.dsActual} ${d.actual.qty ?? '-'}${d.actual.h ? ` · ${t.dsHeight} ${d.actual.h}` : ''}${d.actual.note ? ` · ${d.actual.note}` : ''}`), CW).forEach((ln) => { ensure(5); doc.text(ln, M, y); y += 4.3; }));
    for (const d of (project.drawings || []).filter((x) => x.actual?.photo)) { ensure(42); doc.addImage(d.actual.photo, 'JPEG', M, y, 50, 37); y += 40; }
  }
  };
  if (isProject) planSections();
  if (isProject && pits.length) {
    newPage(projCtx);
    section(t.pitCostTitle);
    const ov = pitOverview(pits, project.reports);
    const cols = [M, M + 36, M + 54, M + 70, M + 90, M + 114, M + 140, M + CW];
    const row = (cells, bold, w = 8.2) => {
      ensure(9); doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(w);
      let lines = 1;
      cells.forEach((v, i) => {
        if (v == null) return;
        const parts = Array.isArray(v) ? v : [v];
        lines = Math.max(lines, parts.length);
        parts.forEach((x, k) => doc.text(fitW(x, colW(cols, i) - 2), cols[i], y + k * 3.8, i === cols.length - 1 ? { align: 'right' } : undefined));
      });
      y += 4.8 + (lines - 1) * 3.8;
    };
    const mt = (r) => (r ? [`${f2(r.m3, t.locale)} m³`, `(${f2(r.t, t.locale)} t)`] : '-');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8.2);
    row([t.pitCount, t.reports, 'm²', t.pitColExcav, t.pitColSand, t.pitColGravel, t.pitColSurface, t.pitColCost].map((h, i) => doc.splitTextToSize(clean(h), colW(cols, i) - 2)), true);
    ov.rows.forEach((r) => row([r.p.name, r.rep, f2(r.c.area, t.locale), f2(r.c.excav.m3, t.locale), mt(r.sand), mt(r.fill), r.surf ? `${f2(r.c.area, t.locale)} m²` : '-', eur(r.c.total, t.locale)]));
    y += 4; section(t.pitByMat);
    const c2 = [M, M + 100, M + 130, M + CW];
    const row2 = (cells, bold) => { ensure(5); doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(9); cells.forEach((v, i) => doc.text(fitW(v, colW(c2, i)), c2[i], y, i === 3 ? { align: 'right' } : undefined)); y += 5; };
    row2([t.pitColLayer, 'm³', 't', t.pitColCost], true);
    Object.values(ov.tot.mats).forEach(({ row: r, m3, t: tn, cost }) => row2([layerName(t, r), f2(m3, t.locale), f2(tn, t.locale), eur(cost, t.locale)]));
    row2([t.pitL_excav, f2(ov.tot.excav, t.locale), '', eur(ov.tot.excavCost, t.locale)]);
    doc.setDrawColor(249, 115, 22); doc.line(c2[2], y - 3, c2[3], y - 3);
    vatBlock(ov.tot.cost, c2[2], c2[3]);
  }

  const checks = isProject ? civilChecks(project, t) : [];
  if (checks.length) {
    y += 4; section(t.ckTitle);
    checks.forEach((x) => {
      ensure(7);
      doc.setDrawColor(40, 40, 40); doc.setLineWidth(0.3); doc.rect(M, y - 3.3, 3.8, 3.8);
      if (x.done) { doc.setLineWidth(0.6); doc.line(M + 0.7, y - 1.4, M + 1.6, y - 0.2); doc.line(M + 1.6, y - 0.2, M + 3.3, y - 2.9); }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.text(clean(`${x.kind === 'pipe' ? t.ckPipe : t.ckPit}: ${x.name}`), M + 6, y);
      doc.setFont('helvetica', 'normal'); doc.text(doc.splitTextToSize(clean(x.text), CW - 75)[0] || '', M + 75, y); y += 5.5;
    });
    para(t.ckHint, 7.5);
  }

  const irr = isProject ? irrSummary(project) : null;
  if (irr && (irr.areas.length || irr.plan.heads.length)) {
    y += 4; section(t.pdfIrrTitle);
    doc.setFontSize(8); para(project.dryStrict ? t.dryStrictOn : t.dryStrictOff, 8);
    irr.areas.forEach(({ g, c }) => {
      ensure(10); doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.text(clean(g.name), M, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(220, 38, 38); doc.text(clean(`${c.dry} % ${t.dryDry}`), M + 60, y);
      doc.setTextColor(202, 138, 4); doc.text(clean(`${c.weak} % ${t.dryWeak}`), M + 90, y);
      doc.setTextColor(22, 163, 74); doc.text(clean(`${c.ok} % ${t.dryOk}`), M + 120, y); doc.setTextColor(20, 20, 20); y += 5;
      if (c.dry > 5) {
        const pp = proposal(g, fittings), ph = pp.ph, prop = pp.heads(ph.r).map((h) => ({ ...h, catId: ph.catId, throw: ph.r })), af = areaCoverage(g, [...fittings, ...prop], project.dryStrict);
        doc.setTextColor(220, 38, 38); para(`! ${t.pdfIrrNotFull.replace('{n}', prop.length).replace('{r}', nf(ph.r, 1)).replace('{ok}', af.ok).replace('{dry}', af.dry)}`, 8.5); doc.setTextColor(20, 20, 20);
      }
    });
    const c = irr.plan.cfg;
    y += 2; para(`${t.izSupply}: ${c.bar ? `${nf(c.bar, 1)} bar` : t.pdfIrrNoBar} · ${c.lpm ? `${nf(c.lpm, 1)} l/min` : t.pdfIrrNoLpm} · ${t.izSoil}: ${t['izSoil_' + c.soil]}`, 8.5);
    irr.plan.rows.forEach((z) => {
      ensure(8); doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5);
      doc.text(doc.splitTextToSize(clean(`${z.free ? t.izFree : z.name} · ${t['izPl_' + z.plant]}`), 60)[0] || '', M, y);
      doc.setFont('helvetica', 'normal');
      doc.text(clean(`${z.heads.length} ${t.izHeads} · ${nf(z.q, 1)} l/min${z.share != null ? ` (${Math.round(z.share * 100)} %)` : ''}${z.pr ? ` · ${nf(z.pr, 1)} mm/h` : ''}${z.min ? ` · ${z.cycles > 1 ? `${z.cycles} x ${Math.ceil(z.min / z.cycles)}` : z.min} min · ${z.days}x ${t.izWeek} · ${hhmm(z.start)}` : ''}`), M + 62, y); y += 4.6;
      z.warn.forEach((w) => { doc.setTextColor(220, 38, 38); para(`! ${t['izW_' + w].replace('{b}', nf(z.needBar, 1)).replace('{n}', z.cycles)}`, 8, M + 4, CW - 4); doc.setTextColor(20, 20, 20); });
    });
  }

  if (isProject && getMode() === 'survey' && fittings.length) {
    newPage(projCtx); section(`${t.pdfCoordTitle} – ${coordTable(t, project).label}`);
    const { head, body } = coordTable(t, project), cx = [M, M + 14, M + 26, M + 64, M + 92, M + 120, M + 137, M + 152, M + 167];
    const row = (c, b) => { ensure(5); doc.setFont('helvetica', b ? 'bold' : 'normal'); doc.setFontSize(7.5); c.forEach((v, i) => doc.text(fitW(String(v ?? ''), (cx[i + 1] || M + CW) - cx[i] - 1), cx[i], y)); y += 4.3; };
    row(head, true); body.forEach((r) => row(r));
  }

  if (isProject && ['detect_inhouse', 'detect_roof'].includes(modeId())) {
    (project.plans || []).forEach((pl) => {
      newPage(projCtx); section(`${t.fpPdfTitle}: ${pl.name}`);
      let w = CW, h = (CW * pl.h) / pl.w;
      if (h > 200) { h = 200; w = (200 * pl.w) / pl.h; }
      const x0 = M + (CW - w) / 2, y0 = y, [[s, wv], [n, e]] = planBounds(pl);
      const P = (la, lo) => [x0 + ((lo - wv) / (e - wv)) * w, y0 + ((n - la) / (n - s)) * h];
      try { doc.addImage(pl.src, 'JPEG', x0, y0, w, h); } catch { /* unreadable plan image */ }
      doc.setDrawColor(148, 163, 184); doc.setLineWidth(0.2); doc.rect(x0, y0, w, h);
      const fl = fittings.filter((f) => f.floor === pl.id), pp = pipes.filter((p) => p.floor === pl.id);
      pp.forEach((p) => {
        const c = (p.color || '#2563eb').replace('#', ''), pts = p.pts.map(([la, lo]) => P(la, lo));
        doc.setDrawColor(parseInt(c.slice(0, 2), 16), parseInt(c.slice(2, 4), 16), parseInt(c.slice(4, 6), 16)); doc.setLineWidth(0.8);
        pts.slice(1).forEach((q, i) => doc.line(pts[i][0], pts[i][1], q[0], q[1]));
      });
      doc.setFontSize(6.5);
      fl.forEach((f, i) => {
        const [x, yy] = P(f.lat, f.lon), c = (f.color || FITTING_TYPES[f.type] || '#dc2626').replace('#', '');
        doc.setFillColor(parseInt(c.slice(0, 2), 16), parseInt(c.slice(2, 4), 16), parseInt(c.slice(4, 6), 16)); doc.circle(x, yy, 1.3, 'F');
        doc.setTextColor(15, 23, 42); doc.text(String(i + 1), x + 1.8, yy - 1);
      });
      y = y0 + h + 5; doc.setTextColor(20, 20, 20);
      const mpins = moistPins(doc, wallGroups(project.moist), pl.id, P, [x0, y0, x0 + w, y0 + h]);
      para(`${t.fpSize}: ${nf(pl.w * pl.mpp, 1)} × ${nf(pl.h * pl.mpp, 1)} m · ${fl.length} ${t.fpPdfPts} · ${pp.length} ${t.fpPdfLines}`, 8.5);
      if (mpins.length) { ensure(6); doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.text(clean(mo2().pinT), M, y); y += 4.5; doc.setFont('helvetica', 'normal'); mpins.forEach((s) => { ensure(5); doc.setFontSize(8); doc.text(clean(s).slice(0, 130), M, y); y += 4.2; }); y += 2; }
      fl.forEach((f, i) => {
        ensure(5); doc.setFontSize(8); doc.text(clean(`${i + 1}  ${f.name}${f.note ? ` – ${f.note}` : ''}`).slice(0, 120), M, y); y += 4.2;
        if (!(f.photos || []).length) return;
        ensure(32);
        f.photos.slice(0, 4).forEach((ph, k) => { try { const ip = doc.getImageProperties(ph.src), s = Math.min(40 / ip.width, 30 / ip.height); doc.addImage(ph.src, 'JPEG', M + 6 + k * 42, y, ip.width * s, ip.height * s); } catch { /* unreadable photo */ } });
        y += 33;
      });
    });
    for (const m of project.moist || []) {
      newPage(projCtx); section(`${t.moTitle}: ${m.name}`);
      await moistBlock(doc, m, { M, CW, t, getY: () => y, setY: (v) => { y = v; }, ensure, maxH: 160 });
    }
    for (const s of wallGroups(project.moist).filter((g) => g.length > 1)) {
      newPage(projCtx); section(`${mo2().trend}: ${s[0].name}`);
      await moistTrendBlock(doc, s, { M, CW, t, getY: () => y, setY: (v) => { y = v; }, ensure, maxH: 110 });
    }
    (project.panos || []).forEach((pn) => {
      newPage(projCtx); section(`${t.pnTitle}: ${pn.name}`);
      const w = CW, h = CW / 2, x0 = M, y0 = y;
      try { doc.addImage(pn.src, 'JPEG', x0, y0, w, h); } catch { /* unreadable photo */ }
      doc.setFontSize(7);
      (pn.marks || []).forEach((mk, i) => { const [u, v] = panoXY(mk), x = x0 + u * w, yy = y0 + v * h; doc.setFillColor(220, 38, 38); doc.circle(x, yy, 1.8, 'F'); doc.setTextColor(255, 255, 255); doc.text(String(i + 1), x, yy + 0.9, { align: 'center' }); });
      doc.setTextColor(20, 20, 20); y = y0 + h + 5;
      (pn.marks || []).forEach((mk, i) => { ensure(5); doc.setFontSize(8); doc.text(clean(`${i + 1}  ${mk.text}`).slice(0, 120), M, y); y += 4.2; });
    });
  }

  // ---- REPORT PAGES ----
  for (const r of reports) {
    const num = project.reports.indexOf(r) + 1;
    const rc = { kind: 'report', top: 42, num, name: r.title, date: fmt(r.updatedAt), coords: `${r.lat.toFixed(6)}, ${r.lon.toFixed(6)}` };
    if (doc.getNumberOfPages() === 1 && !isProject) setCtx(rc); else newPage(rc);
    kv(t.pdfReportPoint, `#${num} ${r.title}`);
    kv(t.pdfDate, fmt(r.updatedAt));
    if (r.address) kv(t.addrLabel, r.address);
    kv(t.pdfCoords, r.gnss ? `${r.lat.toFixed(8)}, ${r.lon.toFixed(8)}` : `${rc.coords}  (Zoom ${Math.round(r.zoom || 18)})`);
    if (r.gnss) kv('GNSS', gnssLine(r.gnss, t));
    kv(t.pdfAngle, `${nf(r.overlayAngle || 0, 1)}°`);
    const job = r.job || {}, ev = job.ev || {}, crew = getCrew();
    if (job.customer || job.order || ev.start || (job.staff || []).length || (job.devices || []).length) {
      y += 2; section(t.wlTitle);
      if (job.customer) kv(t.wlCustomer, job.customer);
      if (job.order) kv(t.wlOrder, job.order);
      if (job.contact) kv(t.wlContact, job.contact);
      const clk = (ms) => (ms ? new Date(ms).toLocaleTimeString(t.locale, { hour: '2-digit', minute: '2-digit' }) : '–');
      if (Object.keys(ev).length) kv(t.wlTimes, STEPS.map((k) => `${t['wl_' + k]} ${clk(ev[k])}`).join(' · '));
      const tm = jobTimes(job);
      if (ev.start) kv(t.wlSum, `${t.wlWork} ${hm(tm.work)} · ${t.wlPauses} ${hm(tm.pause)} · ${t.wlTravel} ${hm(tm.travel)}`);
      const st = crew.staff.filter((x) => (job.staff || []).includes(x.id)).map((x) => `${x.name}${x.role ? ` (${x.role})` : ''}${Number(x.rate) > 0 ? ` ${x.rate} ${cur()}/h` : ''}`);
      const dv = crew.devices.filter((x) => (job.devices || []).includes(x.id)).map((x) => [x.type, x.maker, x.serial, x.calib && `${t.wlCalib} ${x.calib}`, calDue(x) && `(${t.wlCalDue})`].filter(Boolean).join(' '));
      if (st.length) kv(t.wlStaff, st.join(', '));
      const cost = staffCost(job, crew);
      if (cost != null) kv(t.wlCost, `${cost.toLocaleString(t.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur()}`);
      if (dv.length) para(`${t.wlDevices}: ${dv.join('; ')}`, 9);
    }
    y += 2; section(t.pdfReportText);
    para(r.text || t.noText);
    y += 4;
    const snap = await mapSnapshot(r, pipes, { fittings, netPipes: project.pipes, t, pits, drawings: project.drawings, camDirs: geoPhotos([r]) });
    if (snap) {
      section(t.pdfMapTitle);
      const h = (CW * 460) / 720;
      ensure(h + 4); doc.addImage(snap, 'JPEG', M, y, CW, h); y += h + 4;
      legend();
    }
    const mat = {};
    const live = getPrices();
    r.photos.forEach((p) => Object.entries(p.inventory || {}).forEach(([k, n]) => {
      const price = p.inventoryPrices?.[k] ?? findPrice(live, k);
      mat[k] = mat[k] || { n: 0, price, amount: 0 };
      mat[k].n += n; if (price != null) mat[k].amount += n * price;
    }));
    if (Object.keys(mat).length) {
      section(t.pdfMaterial);
      const cols = [M, M + 85, M + 115, M + CW];
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
      doc.text(clean(t.colType), cols[0], y); doc.text(clean(t.colCount), cols[1], y); doc.text(clean(t.colUnitPrice), cols[2], y); doc.text(clean(t.colAmount), cols[3], y, { align: 'right' });
      y += 5; doc.setFont('helvetica', 'normal');
      let total = 0;
      Object.entries(mat).forEach(([k, m]) => {
        ensure(5);
        doc.text(clean(k), cols[0], y); doc.text(String(m.n), cols[1], y);
        doc.text(m.price != null ? clean(eur(m.price, t.locale)) : '–', cols[2], y);
        doc.text(m.price != null ? clean(eur(m.amount, t.locale)) : '–', cols[3], y, { align: 'right' });
        total += m.amount; y += 5;
      });
      doc.setDrawColor(230, 81, 0); doc.line(cols[2], y - 3, cols[3], y - 3);
      doc.setFont('helvetica', 'bold'); doc.text(clean(t.invSum), cols[2], y + 1); doc.text(clean(eur(total, t.locale)), cols[3], y + 1, { align: 'right' });
      y += 8;
    }
    const rMat = materialRows(project, isProject || project.reports.length > 1 ? r.id : null);
    if (rMat.rows.length && getMode() !== 'survey') { section(t.matList); matTable(rMat); }
    const dly = r.daily, dt = dailyTotals(dly);
    if (dly && (dly.weather || dly.temp || dt.total > 0 || (dly.crew || []).length || (dly.mach || []).length)) {
      section(t.drTitle);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(20, 20, 20);
      if (dly.weather || dly.temp) { ensure(6); doc.text(clean(`${t.drWeather}: ${dly.weather ? t['drW_' + dly.weather] : '-'}${dly.temp ? ` · ${dly.temp} °C` : ''}`), M, y); y += 5.5; }
      const cols = [M, M + 70, M + 95, M + 120, M + CW];
      const line = (c, bold) => { ensure(6); doc.setFont('helvetica', bold ? 'bold' : 'normal'); c.forEach((v, i) => doc.text(clean(String(v)), cols[i], y, i === 4 ? { align: 'right' } : undefined)); y += 5; };
      const num = (v) => (Number.isFinite(+v) ? +v : 0);
      [[t.drCrew, dly.crew || [], true], [t.drMach, dly.mach || [], false]].forEach(([title, list, crew]) => {
        if (!list.length) return;
        line([title, crew ? t.drCount : '', t.drHours, `${cur()}/h`, t.colAmount], true);
        list.forEach((x) => line([t['dr_' + x.kind] || x.kind, crew ? x.n || 1 : '', x.h || 0, x.rate || '-', `${(num(crew ? x.n || 1 : 1) * num(x.h) * num(x.rate)).toFixed(2)} EUR`]));
      });
      line([t.drSum, '', `${dt.crewH + dt.machH} h`, '', `${dt.total.toFixed(2)} EUR`], true);
      y += 2;
    }
    const rPits = pits.filter((p) => p.reportId === r.id || (!isProject && !p.reportId));
    if (rPits.length) { section(t.pitSection); rPits.forEach(pitTable); }
    maintSection(maintObjs(isProject || project.reports.length > 1 ? r.id : null));
    const rm = memoText(r.memos);
    if (rm) { section(t.memoTitle); para(rm, 9.5); }
    photoGallery(objPhotos((o) => (isProject || project.reports.length > 1 ? o.reportId === r.id : true)));
    for (let i = 0; i < r.photos.length; i++) {
      const p = r.photos[i];
      const role = p.kind === 'video' ? t.video : t[ROLE_KEYS[p.role] || 'roleGeneral'];
      await imagePage(rc, p, `${t.bnDetail} #${num} (${i + 1}/${r.photos.length}) – ${role}${p.caption ? ' – ' + p.caption : ''}`);
    }
  }

  // ---- MOISTURE MAPS in single reports (project reports have them with the plans) ----
  if (!isProject) for (const m of project.moist || []) { newPage(); section(`${t.moTitle}: ${m.name}`); await moistBlock(doc, m, { M, CW, t, getY: () => y, setY: (v) => { y = v; }, ensure, maxH: 160 }); }
  if (!isProject) for (const s of wallGroups(project.moist).filter((g) => g.length > 1)) { newPage(); section(`${mo2().trend}: ${s[0].name}`); await moistTrendBlock(doc, s, { M, CW, t, getY: () => y, setY: (v) => { y = v; }, ensure, maxH: 110 }); }
  objPhotoAppendix(doc, project, { M, CW, getY: () => y, setY: (v) => { y = v; }, ensure, newPage: () => newPage(isProject ? projCtx : undefined), section });


  // ---- COMPONENT PHOTOS not tied to an exported report (project) ----
  if (isProject) {
    const rids = new Set(reports.map((r) => r.id)), rest = objPhotos((o) => !rids.has(o.reportId));
    if (rest.length) { newPage(projCtx); photoGallery(rest); }
  }

  // ---- MATERIAL OVERVIEW (project) ----
  const entries = isProject ? countEntries(reports) : [];
  if (entries.length) {
    newPage(projCtx);
    section(t.pdfMatOverview);
    const cols = [M, M + 62, M + 112, M + 132, M + CW];
    const row = (cells, bold) => {
      ensure(5); doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(9);
      cells.forEach((c, i) => c != null && doc.text(fitW(c, colW(cols, i)), cols[i === 4 ? 4 : i], y, i === 4 ? { align: 'right' } : undefined));
      y += 5;
    };
    row([t.reports, t.colType, t.colCount, t.colUnitPrice, t.colAmount], true);
    entries.forEach((e) => {
      e.items.forEach((it, k) => row([k ? '' : `${e.report} (${new Date(e.date).toLocaleDateString(t.locale)})`, it.type, it.n, it.price != null ? eur(it.price, t.locale) : '-', it.amount != null ? eur(it.amount, t.locale) : '-']));
      doc.setDrawColor(225); doc.line(M, y - 3.5, M + CW, y - 3.5);
    });
    y += 4; section(t.histTotals);
    const tot = countTotals(entries);
    row([t.colType, '', t.colCount, '', t.colAmount], true);
    tot.rows.forEach((r) => row([r.type, '', r.n, '', r.priced ? eur(r.amount, t.locale) : '-']));
    doc.setDrawColor(230, 81, 0); doc.line(cols[2], y - 3, cols[4], y - 3);
    row([t.invSum, '', tot.pieces, '', eur(tot.total, t.locale)], true);
  }

  if ((project.gardens || []).length) {
    if (isProject) newPage(projCtx); else { ensure(30); y += 4; }
    section(t.gaTitle);
    project.gardens.forEach((g) => {
      const r = rowCalc(g.pts, g.row), l = landCalc(g.pts, g.land), w = (s) => doc.splitTextToSize(clean(s), CW).forEach((ln) => { ensure(5); doc.text(ln, M, y); y += 4.4; });
      ensure(10); doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); w(`${g.name}: ${nf(r.A, 1)} m2 · ${t.gaPer} ${nf(r.per, 1)} m`);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
      if (g.row) w(`${t.gaTabRow}: ${r.rows} x ${nf(r.rowLen, 1)} m · ${t.gaPlants} ${r.plants} · ${t.gaEndPosts} ${r.endPosts} + ${r.anchors} ${t.gaAnchors} · ${t.gaMidPosts} ${r.midPosts} · ${t.gaDripLen} ${nf(r.drip, 1)} m · ${t.gaMain} ${nf(r.main, 1)} m · ${t.gaNet} ${nf(r.net, 1)} m2 · ${t.gaFenceLen} ${nf(r.fence, 1)} m / ${r.fencePosts} ${t.gaFencePosts}`);
      if (g.land) w(`${t.gaTabLand}: ${t.ga_turf} ${nf(l.turf, 1)} m2 · ${t.ga_topsoil} ${nf(l.topsoil.m3, 2)} m3 (${nf(l.topsoil.t, 1)} t) · ${t.ga_mulch} ${nf(l.mulch.m3, 2)} m3 · ${t.ga_gravel} ${nf(l.gravel.m3, 2)} m3 (${nf(l.gravel.t, 1)} t) · ${t.ga_paving} ${nf(l.paving, 1)} m2 · ${t.gaEdging} ${nf(l.edging, 1)} lfm`);
      y += 2;
    });
  }
  const fzs = (project.facades || []).filter((z) => z.out && (isProject || !z.repId || reports.some((r) => r.id === z.repId)));
  if (fzs.length) {
    if (isProject) newPage(projCtx); else { ensure(30); y += 4; }
    section(t.fzTitle);
    for (const z of fzs) {
      const r = z.out, im = await loadImg(z.img, false);
      ensure(10); doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.text(clean(z.name), M, y); y += 5;
      if (im) { const w = Math.min(CW, 120), h = Math.min(90, (w * im.height) / im.width); ensure(h + 4); doc.addImage(z.img, 'JPEG', M, y, (h * im.width) / im.height, h); y += h + 4; }
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
      const L = [
        `${t.fzGross}: ${r.gross.toFixed(2)} m2 · ${t.fzDed}: -${r.ded.toFixed(2)} m2 (${r.rows.filter((x) => x.over).length} ${t.fzOver}) · ${t.fzNet}: ${r.wall.toFixed(2)} m2 · ${t.fzReveal}: ${r.lfm.toFixed(2)} lfm / ${r.reveal.toFixed(2)} m2`,
        ...r.rows.map((x, i) => `  ${t.fzCut} ${i + 1}: ${x.w.toFixed(2)} x ${x.h.toFixed(2)} m = ${x.A.toFixed(2)} m2${x.over ? ` (${t.fzOver})` : ''}`),
        `${t.fzPaint}: ${r.paint.toFixed(1)} l + ${t.fzPrimer} ${r.primer.toFixed(1)} l (${r.buckets} x 12,5 l)`,
        `${t.fzWdvs}: ${r.wdvs.toFixed(2)} m2 · ${t.fzGlue} ${r.glue.toFixed(0)} kg (${r.bags} x 25 kg) · ${t.fzMesh} ${r.mesh.toFixed(2)} m2 · ${r.dowels} ${t.fzDowels}`,
        `${t.fzScaffold}: ${r.sW.toFixed(2)} x ${r.sH.toFixed(2)} m = ${r.sA.toFixed(2)} m2 · ${r.lifts} ${t.fzLifts} · ${r.anchors} ${t.fzAnchors} · ${r.jacks} ${t.fzJacks} · ${r.net.toFixed(2)} m2 ${t.fzNet2}`,
      ];
      L.forEach((s0) => doc.splitTextToSize(clean(s0), CW).forEach((ln) => { ensure(5); doc.text(ln, M, y); y += 4.3; }));
      y += 3;
    }
  }
  if ((project.stakes || []).length) {
    if (isProject) newPage(projCtx); else { ensure(30); y += 4; }
    section(t.skTitle);
    doc.setFontSize(8.5);
    const sst = stakeStats(project.stakes);
    if (sst) { doc.setFont('helvetica', 'bold'); doc.text(clean(`${sst.n} · Ø ${dm(sst.mean)} · max ${dm(sst.max)} · ${t.skInTol}: ${sst.ok}/${sst.judged}`), M, y); y += 5; doc.setFont('helvetica', 'normal'); }
    project.stakes.forEach((s, i) => doc.splitTextToSize(clean(`${i + 1}. ${s.name} · ${new Date(s.at).toLocaleString(t.locale)} · ${FIX_SHORT[s.fix] || s.fix || '-'}${s.acc ? ` ±${dm(s.acc)}` : ''} · Soll ${s.target.lat.toFixed(8)}, ${s.target.lon.toFixed(8)}${s.target.z != null ? ` / ${s.target.z.toFixed(3).replace('.', ',')} m` : ''} · Ist ${s.actual.lat.toFixed(8)}, ${s.actual.lon.toFixed(8)}${s.actual.z != null ? ` / ${s.actual.z.toFixed(3).replace('.', ',')} m` : ''} · dN ${dm(s.dN)} · dE ${dm(s.dE)} · Δ ${dm(s.d)}${s.dz != null ? ` · ΔH ${dm(s.dz)}` : ''}${s.target.zDem != null ? ` · ${t.skZDemShort} ${s.target.zDem.toFixed(1).replace('.', ',')} m` : ''}${s.draft ? ` · !! ${t.prDraft}` : ''}${s.tolOk == null ? '' : s.tolOk ? ` · ✔ ${t.skInTol}` : ` · ✘ ${t.skOutTol}`}${s.gnss?.qs === 'exception' ? ` · ${t.qs_exception}: ${s.gnss.reason || ''}` : ''}${s.gnss?.sim ? ` · ${t.qs_sim}` : ''}`), CW).forEach((ln) => { ensure(5); doc.text(ln, M, y); y += 4.2; }));
    y += 3;
  }
  const prm = pressureMap(project.pressure, project.pipes, fittings);
  const lks = fittings.filter((f) => f.type === 'leak' && leakFlow({ ...f.leakCalc, bar: f.leakCalc?.bar ?? (prm?.nodes[f.id]?.ist ?? prm?.nodes[f.id]?.bar) }).ls > 0);
  if (prm || lks.length) {
    if (isProject) newPage(projCtx); else { ensure(30); y += 4; }
    section(t.prPdfTitle);
    const txt = (v, bold, col = [20, 20, 20]) => { ensure(6); doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(8.5); doc.setTextColor(...col); doc.splitTextToSize(clean(v), CW).forEach((ln) => { ensure(5); doc.text(ln, M, y); y += 4.4; }); };
    const b2 = (v) => `${v.toFixed(2)} bar`;
    if (prm) {
      txt(`${t.prRef}: ${b2(prm.p0)} @ ${prm.src.name} (${prm.z0.toFixed(2)} m) · PN ${prm.pn} · ${t.prMin} ${b2(prm.min)}`, true);
      Object.entries(prm.nodes).forEach(([id, n]) => {
        const f = fittings.find((x) => x.id === id);
        const diag = [n.status === 'over' ? t.prOver.replace('{x}', `PN ${prm.pn}`) : '', n.status === 'low' ? t.prLow.replace('{x}', b2(prm.min)) : '', n.leak ? t.prLeakDiag.replace('{x}', n.drop.toFixed(1)) : '', n.quiet ? t.prQuiet : ''].filter(Boolean).join(' | ');
        txt(`${f.name}: ${n.z.toFixed(2)} m · ${t.prSoll} ${b2(n.bar)}${n.ist != null ? ` · ${t.prIst} ${b2(n.ist)} · Δ ${b2(n.drop)}` : ''}${diag ? ` -> ${diag}` : ''}`, false, n.leak || n.status === 'over' ? [200, 30, 30] : n.status === 'low' ? [180, 100, 0] : [20, 20, 20]);
      });
      y += 3;
    }
    lks.forEach((f) => {
      const c = f.leakCalc, r = leakFlow({ ...c, bar: c.bar ?? (prm?.nodes[f.id]?.ist ?? prm?.nodes[f.id]?.bar) });
      txt(`${t.lkTitle} – ${f.name}: ${t['lkGeo_' + (c.geo || 'round')]} · ${r.area.toFixed(0)} mm² · Cd ${c.cd === 'free' ? 0.62 : 0.45}`, true);
      txt(`${r.ls.toFixed(2)} l/s · ${r.lmin.toFixed(1)} l/min · ${r.m3h.toFixed(2)} m3/h · ${r.m3d.toFixed(1)} m3/d · ${r.m3a.toFixed(0)} m3/a · ${t.lkLoss} ${r.eur.toFixed(0)} EUR/a`);
      txt(`${document.documentElement.lang === 'de' ? 'Spanne' : 'Range'} ${r.lo.toFixed(2).replace('.', ',')} – ${r.hi.toFixed(2).replace('.', ',')} l/s (${(r.lo * 31536).toFixed(0)} – ${(r.hi * 31536).toFixed(0)} m3/a) · Cd 0,45–0,62 · A ±20 % · p ±10 %`);
      if (r.verdict) txt(r.verdict === 'ok' ? `${t.lkOk} (${(r.ratio * 100).toFixed(0)} %)` : r.verdict === 'over' ? t.lkOver.replace('{x}', (r.ratio * 100).toFixed(0)) : t.lkMiss.replace('{x}', r.ls.toFixed(1)).replace('{p}', (r.ratio * 100).toFixed(0)).replace('{y}', r.rest.toFixed(1)), true, r.verdict === 'ok' ? [22, 140, 60] : [200, 30, 30]);
      y += 2;
    });
    if (lks.length) { ensure(16); doc.setDrawColor(220, 120, 0); doc.setFillColor(255, 247, 230); const L = doc.splitTextToSize(clean(t.lkDisc), CW - 4); doc.rect(M, y - 3, CW, L.length * 4.2 + 3, 'FD'); doc.setFontSize(8); doc.setTextColor(150, 70, 0); L.forEach((ln) => { doc.text(ln, M + 2, y + 1); y += 4.2; }); y += 4; }
  }
  const pc = project.cad?.check ? planCheck(project) : [];
  if (pc.length) {
    const tol = project.cad.tol ?? 1, mm = (v) => `${v.toFixed(2)} m`;
    if (isProject) newPage(projCtx); else { ensure(30); y += 4; }
    section(`${t.pcTitle} (± ${tol} m)`);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(60, 60, 60);
    doc.text(clean(`${t.pcPdfRef}: ${project.cad.name} · ${pc.filter((r) => !r.ok).length} / ${pc.length} ${t.pcBad}`), M, y); y += 6;
    const cols = [M, M + 62, M + 92, M + 117, M + 142, M + CW];
    const row = (c, bold, bad) => {
      ensure(6);
      doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(8.5);
      c.forEach((v, i) => { doc.setTextColor(...(i === 4 && bad != null ? (bad ? [200, 30, 30] : [22, 140, 60]) : [20, 20, 20])); doc.text(fitW(v, i < 5 ? colW(cols, i) : 30), cols[i], y); });
      y += 5;
    };
    row([t.pcColPipe, 'DN', t.pcColLen, t.pcColMax, t.pcColDev], true);
    pc.forEach((r) => { row([r.name, r.dn || '-', mm(r.len), mm(r.max), r.ok ? `OK` : mm(r.devLen)], false, !r.ok); doc.setDrawColor(225); doc.line(M, y - 3.5, M + CW, y - 3.5); });
    y += 4;
  }
  const cps = (project.controls || []).filter((c) => c.meas?.length);
  if (cps.length) {
    const tol = cpTol(project);
    if (isProject) newPage(projCtx); else { ensure(30); y += 4; }
    section(`${t.cpTitle} (${t.cpDH} ± ${cpCm(tol.h)} · ${t.cpDV} ± ${cpCm(tol.v)})`);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(60, 60, 60);
    const note = doc.splitTextToSize(clean(t.cpPdfNote), CW); doc.text(note, M, y); y += note.length * 3.8 + 2;
    const cols = [M, M + CW * 0.16, M + CW * 0.42, M + CW * 0.66, M + CW * 0.8, M + CW];
    const row = (c, bold, ok) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(7);
      const L = c.map((v, i) => doc.splitTextToSize(clean(String(v)), cols[i + 1] - cols[i] - 1.5));
      const h = Math.max(...L.map((l) => l.length)) * 3.2 + 1.6;
      ensure(h + 2);
      L.forEach((l, i) => { doc.setTextColor(...(i === 4 && ok != null ? (ok ? [22, 140, 60] : [200, 30, 30]) : [20, 20, 20])); doc.text(l, cols[i], y); });
      y += h;
    };
    row([t.cpName, t.cpColTarget, t.cpColMeas, `${t.cpDH} / ${t.cpDV}`, t.cpColRes], true);
    cps.forEach((cp) => cp.meas.forEach((m) => {
      const r = cpEval(cp, m.g, tol);
      row([cp.name, `${cp.E.toFixed(3)} / ${cp.N.toFixed(3)}${cp.h != null ? ` / ${cp.h.toFixed(3)}` : ''}`, `${new Date(m.ts).toLocaleString(t.locale)} · ${FIX_SHORT[m.g.fix] || m.g.fix} ${m.g.acc != null ? `± ${m.g.acc.toFixed(3).replace('.', ',')} m` : t.gnssAccUnknown} · ${m.g.epochs}×`,
        `${cpCm(r.dH)} / ${cpCm(r.dV)}`, `${r.ok ? t.cpPass : t.cpFail}${m.g.qs === 'exception' ? ` (${t.qs_exception})` : ''}${m.g.sim ? ` (${t.cpSimNo})` : ''}`], false, r.ok);
      doc.setDrawColor(225); doc.line(M, y - 3.4, M + CW, y - 3.4);
    }));
    y += 4;
  }
  if (!isProject) planSections();
  const sig = project.signature;
  if (sig?.data) {
    ensure(48);
    y += 4;
    doc.setDrawColor(13, 71, 161); doc.setLineWidth(0.3); doc.rect(M, y, 90, 40);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(13, 71, 161);
    doc.text(clean(ml(t, 'sigTitle')), M + 2, y + 5);
    try { const p = doc.getImageProperties(sig.data), k = Math.min(86 / p.width, 22 / p.height); doc.addImage(sig.data, p.fileType || 'PNG', M + 2, y + 7, p.width * k, p.height * k, undefined, 'FAST'); } catch { /* bad image */ }
    doc.setDrawColor(150); doc.line(M + 2, y + 31, M + 88, y + 31);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(30, 30, 30);
    doc.text(clean(`${sig.name || ''}${sig.role ? ` (${sig.role})` : ''}`), M + 2, y + 35);
    doc.text(clean(new Date(sig.at).toLocaleString(t.locale)), M + 2, y + 38.5);
    y += 44;
  }
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    const c = meta[p] || ctx;
    if (c.kind === 'project') projectBanner(); else reportBanner(c);
    doc.setDrawColor(13, 71, 161); doc.setLineWidth(0.4); doc.line(M, PH - 13, PW - M, PH - 13);
    doc.setTextColor(13, 71, 161); doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    doc.setFontSize(7); doc.text(clean(t.pdfCreated), M, PH - 8); doc.setFontSize(8);
    if (coLine) { doc.setFontSize(7.5); doc.setTextColor(40, 40, 40); doc.text(fitW(coLine, PW - 2 * M), PW / 2, PH - 15, { align: 'center' }); doc.setFontSize(8); }
    doc.setTextColor(120, 120, 120);
    doc.text(`${clean(t.pdfPage)} ${p} / ${pages}`, PW - M, PH - 8, { align: 'right' });
  }
  if (opts.blob) return doc.output('blob');
  doc.save(pdfName(project, reports, opts));
}
